import React, { useEffect, useState } from "react";
import {
  Lock, LogOut, ArrowLeft, ArrowRight, ChevronDown, Download, Paperclip, X, Trash2, Star, Pencil, Check, Copy, Archive, ArchiveRestore, FileSignature, Sparkles, Eye, Repeat, Users, Banknote,
} from "lucide-react";
import { toast } from "sonner";
import api, { adminApi, API, fmtUSD, formatApiError } from "@/lib/api";
import { DealSchedule } from "./DealSchedule";
import { DocVault } from "./DocVault";
import { DeckEditor } from "./DeckEditor";
import { DealDetails } from "./DealDetails";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const STATUS_OPTS = ["pending", "sent", "received"];
const STATUS_LABEL = { pending: "Pending", sent: "Wire Sent", received: "Funds Received" };
const STATUS_ROW = { pending: "bg-blue-100", sent: "bg-yellow-100", received: "bg-green-100" };
const fmt2 = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n) || 0);
const SECTIONS = [
  { id: "overview", label: "Overview" },
  { id: "deals", label: "Deals" },
  { id: "investors", label: "Investors" },
  { id: "payouts", label: "Payouts" },
  { id: "payables", label: "Payables" },
  { id: "comms", label: "Communications" },
  { id: "vault", label: "Vault" },
  { id: "wire", label: "Account" },
];
const WIRE_FIELDS = [
  ["bank_name", "Bank Name"], ["account_name", "Account Name"], ["account_number", "Account Number"],
  ["routing_number", "Routing Number"], ["swift", "SWIFT / BIC"], ["bank_address", "Bank Address"],
  ["reference", "Reference / Memo"], ["state_of_formation", "State of Formation"], ["notes", "Notes"],
];
const BANK_FIELDS = [
  ["bank_name", "Bank Name"], ["account_name", "Account Holder"], ["account_number", "Account Number"],
  ["routing_number", "Routing Number"], ["swift", "SWIFT / BIC"], ["bank_address", "Bank Address"], ["notes", "Notes"],
];

const field = "rounded-none border-black h-11 focus-visible:ring-0 focus-visible:border-black";

export default function Admin({ onBack }) {
  const [authed, setAuthed] = useState(() => !!localStorage.getItem("admin_token"));
  const [section, setSection] = useState("overview");
  const [selectedInvestor, setSelectedInvestor] = useState(null);
  const [deals, setDeals] = useState([]);
  const [people, setPeople] = useState(null);

  const loadDeals = () => adminApi.get("/site/admin/deals").then(({ data }) => setDeals(data.deals)).catch(() => {});
  useEffect(() => { if (authed) { loadDeals(); adminApi.get("/site/admin/config").then(({ data }) => setPeople(data)).catch(() => {}); } }, [authed]);

  const lockAdmin = () => { localStorage.removeItem("admin_token"); onBack(); };

  if (!authed) return <AdminLogin onBack={onBack} onAuthed={() => setAuthed(true)} />;

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 bg-background border-b border-black">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="mono text-sm font-semibold tracking-[0.18em]">CANIHAVEMONEY&nbsp;LLC</div>
            <span className="mono text-[10px] uppercase tracking-[0.2em] border border-black px-2 py-0.5">Admin</span>
          </div>
          <div className="flex items-center gap-6">
            <button data-testid="admin-lock-btn" onClick={lockAdmin} className="inline-flex items-center gap-2 mono text-xs uppercase tracking-[0.2em] text-muted-foreground hover:text-black">
              <LogOut size={14} /> Sign out
            </button>
            <button data-testid="admin-back-btn" onClick={onBack} className="inline-flex items-center gap-2 mono text-xs uppercase tracking-[0.2em] hover:text-muted-foreground">
              <ArrowLeft size={14} /> Home
            </button>
          </div>
        </div>
        <div className="max-w-6xl mx-auto px-6 flex items-center gap-6 overflow-x-auto border-t border-border">
          {SECTIONS.map((s) => (
            <button key={s.id} data-testid={`admin-tab-${s.id}`} onClick={() => { setSection(s.id); setSelectedInvestor(null); }}
              className={`py-3 mono text-[11px] uppercase tracking-[0.2em] border-b-2 -mb-px transition-colors ${section === s.id ? "border-black text-black" : "border-transparent text-muted-foreground hover:text-black"}`}>
              {s.label}
            </button>
          ))}
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-12">
        {section === "overview" && <Overview />}
        {section === "deals" && <DealsSection deals={deals} reload={loadDeals} />}
        {section === "investors" && (
          selectedInvestor
            ? <InvestorDetail id={selectedInvestor} deals={deals} onBack={() => setSelectedInvestor(null)} onChanged={loadDeals} />
            : <InvestorsSection onOpen={setSelectedInvestor} />
        )}
        {section === "payouts" && <PayoutsSection />}
        {section === "payables" && <PayablesSection />}
        {section === "comms" && <CommsSection />}
        {section === "vault" && (
          <div className="border border-black">
            <div className="p-4"><h2 className="text-xl font-black tracking-tighter">Templates Library</h2><p className="mono text-[10px] text-muted-foreground mt-1">Reusable docs across all deals — agreement templates, Heter Izka, broker forms, etc. Per-deal documents live inside each deal on the Deals tab.</p></div>
            <DocVault title="Templates" />
          </div>
        )}
        {section === "wire" && <WireSection />}
      </div>
    </div>
  );
}

function CommsSection() {
  const [folder, setFolder] = useState("inbox");
  const [emails, setEmails] = useState([]);
  const [investors, setInvestors] = useState([]);
  const [deals, setDeals] = useState([]);
  const [q, setQ] = useState("");
  const [composing, setComposing] = useState(false);
  const [form, setForm] = useState({ investor_id: "", deal_id: "", recipient_email: "", subject: "", body: "" });
  const [bcc, setBcc] = useState([]);
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [sending, setSending] = useState(false);
  const [settings, setSettings] = useState(null);
  const [savingNow, setSavingNow] = useState(false);

  const loadEmails = (f) => adminApi.get(`/site/admin/emails?status=${f}`).then(({ data }) => setEmails(data.emails || [])).catch(() => {});
  useEffect(() => { if (!composing) loadEmails(folder); }, [folder, composing]);
  useEffect(() => {
    adminApi.get("/site/admin/investors").then(({ data }) => setInvestors(data.investors || [])).catch(() => {});
    adminApi.get("/site/admin/deals").then(({ data }) => setDeals(data.deals || [])).catch(() => {});
    adminApi.get("/site/admin/email/settings").then(({ data }) => setSettings(data)).catch(() => {});
  }, []);

  const toggleSetting = async (key) => {
    const next = { daily_enabled: settings?.daily_enabled ?? true, weekly_enabled: settings?.weekly_enabled ?? true, [key]: !(settings?.[key] ?? true) };
    setSettings(next);
    try { await adminApi.put("/site/admin/email/settings", next); toast.success(`${key === "daily_enabled" ? "Daily" : "Weekly"} summaries ${next[key] ? "ON" : "OFF"}`); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); adminApi.get("/site/admin/email/settings").then(({ data }) => setSettings(data)).catch(() => {}); }
  };
  const sendDailyNow = async () => {
    if (!window.confirm("Send the daily summary to all eligible investors right now?")) return;
    setSavingNow(true);
    try { const { data } = await adminApi.post("/site/admin/email/daily-summary/run"); toast.success(`Sent ${data.sent} email(s) to ${data.recipients} recipient(s)`); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSavingNow(false); }
  };

  const dealName = (id) => (deals.find((d) => d._id === id) || {}).name || "—";
  const pickInvestor = (id) => { const inv = investors.find((i) => i._id === id); setForm((f) => ({ ...f, investor_id: id, recipient_email: inv ? inv.email : f.recipient_email })); };

  const resetCompose = () => { setForm({ investor_id: "", deal_id: "", recipient_email: "", subject: "", body: "" }); setBcc([]); setAiPrompt(""); };

  const bccWholeDeal = async () => {
    if (!form.deal_id) return toast.error("Pick a deal first");
    try {
      const { data } = await adminApi.get(`/site/admin/deals/${form.deal_id}/investments`);
      const found = [...new Set((data.investments || []).map((i) => (i.email || "").trim().toLowerCase()).filter(Boolean))];
      if (!found.length) return toast.error("No investor emails found in this deal");
      setBcc((prev) => [...new Set([...prev, ...found])]);
      toast.success(`Added ${found.length} investor(s) to BCC`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const removeBcc = (email) => setBcc((prev) => prev.filter((e) => e !== email));

  const generateAI = async () => {
    if (!aiPrompt.trim()) return toast.error("Describe the email you want");
    setAiBusy(true);
    try {
      const { data } = await adminApi.post("/site/admin/email/generate", { prompt: aiPrompt.trim() });
      setForm((f) => ({ ...f, subject: data.subject || f.subject, body: data.body || f.body }));
      toast.success("Draft generated — review before sending");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setAiBusy(false); }
  };

  const sendEmail = async () => {
    const to = form.recipient_email ? [form.recipient_email] : [];
    if (!to.length && !bcc.length) return toast.error("Pick a recipient or add BCC investors");
    if (!form.subject.trim()) return toast.error("Subject is required");
    setSending(true);
    try {
      const { data } = await adminApi.post("/site/admin/email", { to, bcc, subject: form.subject, body: form.body });
      await adminApi.post("/site/admin/emails", {
        investor_id: form.investor_id, deal_id: form.deal_id,
        recipient_email: to[0] || `BCC · ${bcc.length} investor(s)`,
        subject: form.subject, body: form.body, status: "sent",
      }).catch(() => {});
      toast.success(`Email sent to ${data.recipients} recipient(s)`);
      resetCompose(); setComposing(false); setFolder("sent");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSending(false); }
  };

  const save = async (status) => {
    if (!form.recipient_email && !form.investor_id && !bcc.length) return toast.error("Pick a recipient");
    try {
      await adminApi.post("/site/admin/emails", { investor_id: form.investor_id, deal_id: form.deal_id, recipient_email: form.recipient_email || `BCC · ${bcc.length} investor(s)`, subject: form.subject, body: form.body, status });
      toast.success("Draft saved");
      resetCompose(); setComposing(false); setFolder("draft");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const del = async (id) => { await adminApi.delete(`/site/admin/emails/${id}`).catch(() => {}); loadEmails(folder); };
  const filtered = emails.filter((e) => !q || (e.recipient_email || "").toLowerCase().includes(q.toLowerCase()) || (e.subject || "").toLowerCase().includes(q.toLowerCase()));
  const folders = [["inbox", "Inbox"], ["sent", "Sent"], ["draft", "Drafts"]];

  return (
    <div data-testid="comms-section">
      <h1 className="text-2xl font-black tracking-tighter pb-3 border-b border-black">Investor Communications</h1>

      <div data-testid="comms-summaries" className="border border-black p-5 mt-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Automated Summaries</div>
            <p className="mono text-[11px] text-muted-foreground mt-1 max-w-lg">Portal balance updates emailed to investors. Daily goes out weekdays at 8:00 AM ET · Weekly on Mondays at 8:00 AM ET.</p>
          </div>
          <button data-testid="comms-send-daily-now" onClick={sendDailyNow} disabled={savingNow} className="inline-flex items-center gap-2 border border-black h-9 px-4 mono text-[10px] uppercase tracking-[0.2em] hover:bg-black hover:text-white transition-colors disabled:opacity-50">
            {savingNow ? "Sending…" : "Send Daily Now"}
          </button>
        </div>
        <div className="grid sm:grid-cols-2 gap-4 mt-5">
          {[["daily_enabled", "Daily Summaries", "Weekdays · 8 AM ET"], ["weekly_enabled", "Weekly Summaries", "Mondays · 8 AM ET"]].map(([key, label, sub]) => {
            const on = settings?.[key] ?? true;
            return (
              <div key={key} className="flex items-center justify-between border border-black p-4">
                <div>
                  <div className="font-semibold text-sm">{label}</div>
                  <div className="mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground mt-0.5">{sub}</div>
                </div>
                <button data-testid={`comms-toggle-${key}`} onClick={() => toggleSetting(key)} className={`relative w-14 h-7 border border-black transition-colors ${on ? "bg-black" : "bg-white"}`} aria-pressed={on}>
                  <span className={`absolute top-0.5 h-5 w-5 transition-all ${on ? "left-8 bg-white" : "left-0.5 bg-black"}`} />
                  <span className={`absolute inset-0 flex items-center mono text-[8px] uppercase tracking-[0.15em] ${on ? "justify-start pl-1.5 text-white" : "justify-end pr-1.5 text-black"}`}>{on ? "ON" : "OFF"}</span>
                </button>
              </div>
            );
          })}
        </div>
      </div>

      <div className="flex gap-6 mt-6">
        <div className="w-44 shrink-0 space-y-1">
          {folders.map(([id, label]) => (
            <button key={id} data-testid={`comms-folder-${id}`} onClick={() => { setComposing(false); setFolder(id); }} className={`w-full text-left px-3 h-10 mono text-[11px] uppercase tracking-[0.15em] border border-black transition-colors ${folder === id && !composing ? "bg-black text-white" : "hover:bg-black hover:text-white"}`}>{label}</button>
          ))}
          <button data-testid="comms-folder-compose" onClick={() => setComposing(true)} className={`w-full text-left px-3 h-10 mono text-[11px] uppercase tracking-[0.15em] border-2 border-black transition-colors ${composing ? "bg-black text-white" : "hover:bg-black hover:text-white"}`}>Compose</button>
        </div>
        <div className="flex-1 min-w-0">
          {!composing ? (
            <>
              <div className="flex items-center gap-3 mb-4">
                <input data-testid="comms-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search recipient or subject…" className="flex-1 rounded-none border border-black bg-background h-10 px-3 text-sm focus:outline-none" />
                <button data-testid="comms-new-btn" onClick={() => setComposing(true)} className="inline-flex items-center gap-2 bg-black text-white h-10 px-4 mono text-[10px] uppercase tracking-[0.2em] hover:bg-neutral-800">+ New Email</button>
              </div>
              <div className="border border-black overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="border-b border-black mono text-[9px] uppercase tracking-[0.15em] text-left">
                    <th className="p-3">Recipient</th><th className="p-3">Subject</th><th className="p-3">Deal</th><th className="p-3">Status</th><th className="p-3">Date</th><th className="p-3"></th>
                  </tr></thead>
                  <tbody>
                    {filtered.length === 0 && <tr><td colSpan={6} className="p-6 text-center mono text-[11px] text-muted-foreground">No emails in {folder}.</td></tr>}
                    {filtered.map((e) => (
                      <tr key={e.id} data-testid={`comms-row-${e.id}`} className="border-b border-black/10">
                        <td className="p-3 mono text-[11px]">{e.recipient_email || "—"}</td>
                        <td className="p-3 font-medium">{e.subject || "(no subject)"}</td>
                        <td className="p-3 mono text-[11px]">{dealName(e.deal_id)}</td>
                        <td className="p-3"><span className="mono text-[9px] uppercase tracking-[0.15em] border border-black px-2 py-0.5">{e.status}</span></td>
                        <td className="p-3 mono text-[10px] text-muted-foreground">{(e.created_at || "").slice(0, 10)}</td>
                        <td className="p-3 text-right"><button data-testid={`comms-del-${e.id}`} onClick={() => del(e.id)} className="text-muted-foreground hover:text-destructive"><Trash2 size={14} /></button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <div data-testid="comms-compose" className="border border-black p-6 max-w-2xl space-y-4">
              <h2 className="text-lg font-black tracking-tighter">Compose Email</h2>

              <div className="border border-black bg-neutral-50 p-4">
                <div className="mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground flex items-center gap-2"><Sparkles size={13} /> AI Email Builder</div>
                <textarea data-testid="comms-ai-prompt" value={aiPrompt} onChange={(e) => setAiPrompt(e.target.value)} rows={2} placeholder="e.g. Tell investors their Q3 payout posted and thank them for their trust" className="w-full rounded-none border border-black bg-white p-3 mt-2 text-sm focus:outline-none" />
                <button data-testid="comms-ai-generate" onClick={generateAI} disabled={aiBusy} className="mt-2 inline-flex items-center gap-2 bg-black text-white h-9 px-4 mono text-[10px] uppercase tracking-[0.2em] hover:bg-neutral-800 disabled:opacity-50">
                  <Sparkles size={12} /> {aiBusy ? "Writing…" : "Generate Draft"}
                </button>
              </div>

              <div>
                <Label className="mono text-[10px] uppercase tracking-[0.2em]">To (investor, optional)</Label>
                <select data-testid="comms-to" value={form.investor_id} onChange={(e) => pickInvestor(e.target.value)} className="w-full rounded-none border border-black bg-background h-11 px-3 mt-2 text-sm">
                  <option value="">Select an investor…</option>
                  {investors.map((i) => <option key={i._id} value={i._id}>{i.name} · {i.email}</option>)}
                </select>
              </div>

              <div>
                <Label className="mono text-[10px] uppercase tracking-[0.2em]">Deal</Label>
                <div className="flex gap-2 mt-2">
                  <select data-testid="comms-deal" value={form.deal_id} onChange={(e) => setForm({ ...form, deal_id: e.target.value })} className="flex-1 rounded-none border border-black bg-background h-11 px-3 text-sm">
                    <option value="">No deal</option>
                    {deals.map((d) => <option key={d._id} value={d._id}>{d.name}</option>)}
                  </select>
                  <button data-testid="comms-bcc-deal" onClick={bccWholeDeal} className="shrink-0 inline-flex items-center gap-2 border border-black h-11 px-4 mono text-[10px] uppercase tracking-[0.2em] hover:bg-black hover:text-white transition-colors"><Users size={13} /> BCC Everyone</button>
                </div>
              </div>

              <div>
                <Label className="mono text-[10px] uppercase tracking-[0.2em]">BCC recipients ({bcc.length})</Label>
                <div data-testid="comms-bcc-list" className="mt-2 border border-black min-h-[48px] p-2 flex flex-wrap gap-2">
                  {bcc.length === 0 && <span className="mono text-[11px] text-muted-foreground px-1 py-1">No BCC yet — pick a deal and hit "BCC Everyone".</span>}
                  {bcc.map((e) => (
                    <span key={e} data-testid={`comms-bcc-chip-${e}`} className="inline-flex items-center gap-1.5 bg-black text-white mono text-[10px] px-2 py-1">
                      {e}
                      <button data-testid={`comms-bcc-remove-${e}`} onClick={() => removeBcc(e)} className="hover:text-red-400"><X size={11} /></button>
                    </span>
                  ))}
                </div>
              </div>

              <div>
                <Label className="mono text-[10px] uppercase tracking-[0.2em]">Subject</Label>
                <Input data-testid="comms-subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className={`${field} mt-2`} />
              </div>
              <div>
                <Label className="mono text-[10px] uppercase tracking-[0.2em]">Message</Label>
                <textarea data-testid="comms-body" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} rows={8} className="w-full rounded-none border border-black bg-background p-3 mt-2 text-sm focus:outline-none" />
              </div>
              <div className="flex gap-3">
                <button data-testid="comms-send" onClick={sendEmail} disabled={sending} className="bg-black text-white h-11 px-8 mono text-[10px] uppercase tracking-[0.2em] hover:bg-neutral-800 disabled:opacity-50">{sending ? "Sending…" : "Send"}</button>
                <button data-testid="comms-draft" onClick={() => save("draft")} className="border border-black h-11 px-8 mono text-[10px] uppercase tracking-[0.2em] hover:bg-black hover:text-white">Save Draft</button>
                <button onClick={() => { resetCompose(); setComposing(false); }} className="h-11 px-4 mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Cancel</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


function PayablesSection() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  useEffect(() => { adminApi.get("/site/admin/payables").then(({ data }) => setData(data)).catch(() => {}); }, []);

  const rows = (data?.rows || []).filter((r) => {
    const s = q.trim().toLowerCase();
    return !s || (r.investor_name || "").toLowerCase().includes(s) || (r.deal_name || "").toLowerCase().includes(s);
  });
  const t = data?.totals || {};

  const Bar = ({ pct }) => (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-2 bg-neutral-200 min-w-[60px]"><div className="h-2 bg-black" style={{ width: `${Math.min(pct || 0, 100)}%` }} /></div>
      <span className="mono text-[10px] w-12 text-right">{(pct || 0).toFixed(1)}%</span>
    </div>
  );

  return (
    <div data-testid="admin-payables">
      <div className="flex items-center justify-between pb-3 border-b border-black flex-wrap gap-3">
        <h1 className="text-3xl sm:text-4xl font-black tracking-tighter">Payables</h1>
        <input data-testid="payables-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search investor or deal…" className="rounded-none border border-black bg-background h-10 px-3 text-sm focus:outline-none w-full sm:w-64" />
      </div>
      <p className="mono text-[11px] text-muted-foreground mt-3">Admin-only. What you owe investors across every active position. Figures update daily from each deal's payment schedule.</p>

      <div className="grid grid-cols-2 lg:grid-cols-4 border border-black mt-6">
        <div data-testid="payables-owed" className="border-r border-b lg:border-b-0 border-black p-6 bg-black text-white">
          <div className="mono text-[10px] uppercase tracking-[0.2em] text-neutral-400">Total owed out</div>
          <div className="text-2xl sm:text-3xl font-black tracking-tighter mt-2">{fmtUSD(t.remaining)}</div>
        </div>
        <div className="border-r border-b lg:border-b-0 border-black p-6">
          <div className="mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Principal still owed</div>
          <div className="text-2xl sm:text-3xl font-black tracking-tighter mt-2">{fmtUSD(t.principal_remaining)}</div>
        </div>
        <div className="border-r border-black p-6">
          <div className="mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Paid out to date</div>
          <div className="text-2xl sm:text-3xl font-black tracking-tighter mt-2">{fmtUSD(t.paid)}</div>
        </div>
        <div className="p-6">
          <div className="mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Total payback</div>
          <div className="text-2xl sm:text-3xl font-black tracking-tighter mt-2">{fmtUSD(t.full_return)}</div>
        </div>
      </div>

      {rows.length === 0 ? (
        <div className="border border-black border-dashed p-16 text-center mono text-xs uppercase tracking-[0.2em] text-muted-foreground mt-8">No active positions with a payment schedule.</div>
      ) : (
        <div className="border border-black overflow-x-auto mt-8">
          <table className="w-full text-sm" data-testid="payables-table">
            <thead>
              <tr className="border-b border-black mono text-[9px] uppercase tracking-[0.15em] text-muted-foreground text-left">
                <th className="p-3 font-normal">Investor</th>
                <th className="p-3 font-normal">Deal</th>
                <th className="p-3 font-normal text-right">Principal</th>
                <th className="p-3 font-normal text-right">Full Return</th>
                <th className="p-3 font-normal text-right">Paid</th>
                <th className="p-3 font-normal min-w-[130px]">% to Principal</th>
                <th className="p-3 font-normal min-w-[130px]">% to Full Deal</th>
                <th className="p-3 font-normal text-right">Remaining</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={`${r.investor_id}-${r.deal_id}-${i}`} data-testid={`payables-row-${i}`} className="border-b border-black/10">
                  <td className="p-3 font-medium">{r.investor_name || "—"}</td>
                  <td className="p-3 mono text-[11px] text-muted-foreground">{r.deal_name || "—"}</td>
                  <td className="p-3 text-right mono text-[12px]">{fmtUSD(r.principal)}</td>
                  <td className="p-3 text-right mono text-[12px]">{fmtUSD(r.full_return)}</td>
                  <td className="p-3 text-right mono text-[12px]">{fmtUSD(r.paid)}</td>
                  <td className="p-3"><Bar pct={r.pct_principal} /></td>
                  <td className="p-3"><Bar pct={r.pct_full} /></td>
                  <td className="p-3 text-right font-semibold">{fmtUSD(r.remaining)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr data-testid="payables-totals" className="border-t-2 border-black bg-neutral-50 font-semibold">
                <td className="p-3 mono text-[10px] uppercase tracking-[0.15em]">{t.count} position{t.count === 1 ? "" : "s"}</td>
                <td className="p-3" />
                <td className="p-3 text-right mono text-[12px]">{fmtUSD(t.principal)}</td>
                <td className="p-3 text-right mono text-[12px]">{fmtUSD(t.full_return)}</td>
                <td className="p-3 text-right mono text-[12px]">{fmtUSD(t.paid)}</td>
                <td className="p-3" />
                <td className="p-3 mono text-[10px] text-muted-foreground">Avg {(t.pct_full_avg || 0).toFixed(1)}% to full</td>
                <td className="p-3 text-right text-base">{fmtUSD(t.remaining)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}

function Kpi({ label, value }) {
  return (
    <div className="border-r border-b border-black p-8">
      <div className="mono text-xs uppercase tracking-[0.2em] text-muted-foreground">{label}</div>
      <div className="text-3xl sm:text-4xl font-black tracking-tighter mt-3">{value}</div>
    </div>
  );
}

function Overview() {
  const [s, setS] = useState(null);
  useEffect(() => {
    let active = true;
    const fetchS = () => adminApi.get("/site/admin/summary").then(({ data }) => { if (active) setS(data); }).catch(() => {});
    fetchS();
    const iv = setInterval(fetchS, 4000);
    const onFocus = () => fetchS();
    window.addEventListener("focus", onFocus);
    return () => { active = false; clearInterval(iv); window.removeEventListener("focus", onFocus); };
  }, []);
  if (!s) return <div className="mono text-xs text-muted-foreground animate-pulse">Loading…</div>;
  const f = s.featured;
  return (
    <div data-testid="admin-overview">
      <h1 className="text-4xl sm:text-5xl font-black tracking-tighter">Raise Dashboard</h1>
      <div className="grid grid-cols-2 lg:grid-cols-4 border-l border-t border-black mt-10">
        <Kpi label="Total Committed" value={fmtUSD(s.total_committed)} />
        <Kpi label="Funds In (Sent/Received)" value={fmtUSD(s.total_received)} />
        <Kpi label="Investors" value={s.investor_count} />
        <Kpi label="Investments" value={s.investment_count} />
      </div>
      {(s.deals || []).length > 0 && (
        <div className="mt-10 space-y-4" data-testid="overview-deals">
          <div className="mono text-xs uppercase tracking-[0.2em] text-muted-foreground">All Deals</div>
          {s.deals.map((d) => (
            <div key={d.id} className="border border-black p-6" data-testid={`overview-deal-${d.id}`}>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="text-xl font-black tracking-tighter">{d.name}</div>
                <span className={`mono text-[10px] uppercase tracking-[0.2em] px-3 py-1 border ${d.closed ? "bg-black text-white border-black" : "border-black"}`}>{d.closed ? "Closed" : "Open"}</span>
              </div>
              <div className="flex justify-between mt-4 mb-2 mono text-xs uppercase tracking-[0.2em] text-muted-foreground">
                <span>{d.percent}% of {fmtUSD(d.goal)} goal</span>
                <span>{fmtUSD(d.committed)} / {fmtUSD(d.goal)}</span>
              </div>
              <div className="h-2 w-full bg-muted overflow-hidden"><div className="h-full bg-black" style={{ width: `${d.percent}%` }} /></div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------------- DEALS ---------------- */
function emptyDeal() { return { name: "", goal: "", return_pct: "30", term_days: "65", description: "", status: "open" }; }

function RefiModal({ deal, onClose, onDone, investors }) {
  const [info, setInfo] = useState(null);
  const [kind, setKind] = useState("addon");
  const [name, setName] = useState("");
  const [capital, setCapital] = useState("");
  const [invFactor, setInvFactor] = useState("1.30");
  const [grossFactor, setGrossFactor] = useState("1.50");
  const [fee, setFee] = useState("0");
  const [term, setTerm] = useState("65");
  const [start, setStart] = useState(new Date().toISOString().slice(0, 10));
  const [roll, setRoll] = useState(true);
  const [reinvest, setReinvest] = useState(false);
  const [closeParent, setCloseParent] = useState(true);
  const [rows, setRows] = useState([]);
  const [extraRows, setExtraRows] = useState([]);
  const [pick, setPick] = useState("");
  const [pickAmt, setPickAmt] = useState("");
  const [pickSearch, setPickSearch] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    adminApi.get(`/site/admin/deals/${deal._id}/refi-info`).then(({ data }) => {
      setInfo(data);
      setTerm(String(data.term_days || 65));
      if (data.parent_funded) {
        setInvFactor((data.investor_return / data.parent_funded || 1.3).toFixed(2));
        setGrossFactor((data.gross_return / data.parent_funded || 1.5).toFixed(2));
      }
      setRows((data.investors || []).map((x) => ({ investor_id: x.investor_id, investor_name: x.investor_name, parent_amount: x.amount, remaining: x.remaining, active_balance: x.active_balance || 0, amount: "" })));
    }).catch(() => {});
  }, [deal._id]);

  const parentRemaining = info?.parent_remaining || 0;
  const newCapital = parseFloat(capital) || 0;
  const fundedTotal = newCapital + (kind === "refi" && roll ? parentRemaining : 0);
  const investorReturn = fundedTotal * (parseFloat(invFactor) || 0);
  const grossReturn = fundedTotal * (parseFloat(grossFactor) || 0);
  const existingIds = new Set(rows.map((r) => r.investor_id));
  const extraIds = new Set(extraRows.map((r) => r.investor_id));
  const newMoney = extraRows.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
  const remainder = Math.max(fundedTotal - newMoney, 0);
  const allocated = rows.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0) + newMoney;

  const fillProportional = () => {
    const base = info?.parent_funded || 0;
    setRows(rows.map((r) => ({ ...r, amount: base ? String(Math.round((r.parent_amount / base) * remainder)) : "" })));
  };
  const fillFromActive = () => {
    setRows(rows.map((r) => ({ ...r, amount: r.active_balance ? String(Math.round(r.active_balance)) : "0" })));
    setReinvest(true);
    toast.success("Filled from each investor's paid-to-date balance — tagged as reinvested");
  };
  const addExtra = () => {
    if (!pick) return toast.error("Pick an investor to add");
    const amt = parseFloat(pickAmt) || 0;
    if (amt <= 0) return toast.error("Enter an amount for the new investor");
    const m = (investors || []).find((x) => x._id === pick);
    setExtraRows([...extraRows, { investor_id: pick, investor_name: m ? (m.name || m.email || pick) : pick, amount: String(amt) }]);
    setPick(""); setPickAmt("");
  };
  const removeExtra = (i) => setExtraRows(extraRows.filter((_, j) => j !== i));

  const submit = async () => {
    if (fundedTotal <= 0) return toast.error("Enter new capital (or roll remaining on a refi)");
    if (!term || parseInt(term) <= 0) return toast.error("Enter a term in days");
    setBusy(true);
    try {
      const payload = {
        kind, name, funded: fundedTotal, roll_remaining: kind === "refi" && roll,
        investor_return: investorReturn, gross_return: grossReturn,
        fee_holdback: parseFloat(fee) || 0, term_days: parseInt(term), start_date: start,
        close_parent: kind === "refi" && closeParent,
        investors: [
          ...rows.map((r) => ({ ...r, reinvested: reinvest })),
          ...extraRows.map((r) => ({ ...r, reinvested: false })),
        ].filter((r) => (parseFloat(r.amount) || 0) > 0).map((r) => ({ investor_id: r.investor_id, amount: parseFloat(r.amount), reinvested: r.reinvested })),
      };
      const { data } = await adminApi.post(`/site/admin/deals/${deal._id}/refi`, payload);
      toast.success(`Created "${data.name}" with ${data.investments} investor(s)${data.parent_closed ? " · parent closed" : ""}`);
      onDone(); onClose();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };

  const inp = "rounded-none border border-black bg-background h-10 px-3 mono text-[12px] focus:outline-none w-full";
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center p-4 overflow-y-auto" data-testid="refi-modal" onClick={onClose}>
      <div className="bg-white border-2 border-black w-full max-w-2xl my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-black">
          <div className="mono text-xs uppercase tracking-[0.25em]">Add-On / Refi · {info?.parent_name || deal.name}</div>
          <button data-testid="refi-close" onClick={onClose} className="text-muted-foreground hover:text-black"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-5">
          <div className="grid grid-cols-2 gap-2">
            <button data-testid="refi-kind-addon" onClick={() => setKind("addon")} className={`h-10 mono text-[10px] uppercase tracking-[0.15em] border border-black ${kind === "addon" ? "bg-black text-white" : "hover:bg-neutral-100"}`}>Add-On</button>
            <button data-testid="refi-kind-refi" onClick={() => setKind("refi")} className={`h-10 mono text-[10px] uppercase tracking-[0.15em] border border-black ${kind === "refi" ? "bg-black text-white" : "hover:bg-neutral-100"}`}>Refi</button>
          </div>
          <div>
            <div className="mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground mb-1">New deal name (optional)</div>
            <input data-testid="refi-name" value={name} onChange={(e) => setName(e.target.value)} className={inp} placeholder={`${info?.parent_name || deal.name} (${kind === "refi" ? "Refi" : "Add-On"})`} />
          </div>
          {kind === "refi" && (
            <div className="border border-black p-3 space-y-2">
              <label className="flex items-center gap-2 mono text-[11px]"><input data-testid="refi-roll" type="checkbox" checked={roll} onChange={(e) => setRoll(e.target.checked)} /> Roll remaining <strong>{fmtUSD(parentRemaining)}</strong> from parent into this deal</label>
              <label className="flex items-center gap-2 mono text-[11px]"><input data-testid="refi-close-parent" type="checkbox" checked={closeParent} onChange={(e) => setCloseParent(e.target.checked)} /> Close the parent deal when this is created</label>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div><div className="mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground mb-1">New capital ($)</div><input data-testid="refi-capital" value={capital} onChange={(e) => setCapital(e.target.value.replace(/[^0-9.]/g, ""))} className={inp} placeholder="0" /></div>
            <div><div className="mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground mb-1">Term (days)</div><input data-testid="refi-term" value={term} onChange={(e) => setTerm(e.target.value.replace(/[^0-9]/g, ""))} className={inp} /></div>
            <div><div className="mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground mb-1">Investor factor (e.g. 1.30)</div><input data-testid="refi-inv-factor" value={invFactor} onChange={(e) => setInvFactor(e.target.value.replace(/[^0-9.]/g, ""))} className={inp} /></div>
            <div><div className="mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground mb-1">Gross factor (e.g. 1.50)</div><input data-testid="refi-gross-factor" value={grossFactor} onChange={(e) => setGrossFactor(e.target.value.replace(/[^0-9.]/g, ""))} className={inp} /></div>
            <div><div className="mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground mb-1">Fee holdback ($)</div><input data-testid="refi-fee" value={fee} onChange={(e) => setFee(e.target.value.replace(/[^0-9.]/g, ""))} className={inp} /></div>
            <div><div className="mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground mb-1">Start date</div><input data-testid="refi-start" type="date" value={start} onChange={(e) => setStart(e.target.value)} className={inp} /></div>
          </div>
          <div className="border border-black p-3 mono text-[11px] space-y-1 bg-neutral-50">
            <div className="flex justify-between"><span>Funded (total)</span><strong>{fmtUSD(fundedTotal)}</strong></div>
            {newMoney > 0 && <div className="flex justify-between text-muted-foreground"><span>New money (new investors)</span><span>{fmtUSD(newMoney)}</span></div>}
            {newMoney > 0 && <div className="flex justify-between text-muted-foreground"><span>Remainder split across group</span><span>{fmtUSD(remainder)}</span></div>}
            <div className="flex justify-between"><span>Investor total return</span><strong>{fmtUSD(investorReturn)}</strong></div>
            <div className="flex justify-between text-muted-foreground"><span>Gross repayment (admin)</span><span>{fmtUSD(grossReturn)}</span></div>
          </div>
          <div className="border border-black p-3">
            <div className="mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground mb-2">New money / new investors (optional)</div>
            <input data-testid="refi-extra-search" value={pickSearch} onChange={(e) => setPickSearch(e.target.value)} placeholder="Search investor by name or email…" className="w-full rounded-none border border-black bg-background h-9 px-2 mono text-[11px] mb-2 focus:outline-none" />
            <div className="flex flex-wrap items-center gap-2">
              <select data-testid="refi-extra-select" value={pick} onChange={(e) => setPick(e.target.value)} className="rounded-none border border-black bg-background h-9 px-2 mono text-[11px] min-w-[200px]">
                <option value="">Select investor…</option>
                {(investors || []).filter((x) => !extraIds.has(x._id)).filter((x) => { const s = pickSearch.trim().toLowerCase(); return !s || (x.name || "").toLowerCase().includes(s) || (x.email || "").toLowerCase().includes(s); }).map((x) => <option key={x._id} value={x._id}>{x.name || x.email || x._id}{existingIds.has(x._id) ? " · in group" : ""}</option>)}
              </select>
              <input data-testid="refi-extra-amount" value={pickAmt} onChange={(e) => setPickAmt(e.target.value.replace(/[^0-9.]/g, ""))} className="rounded-none border border-black bg-background h-9 px-2 mono text-[11px] w-32 text-right" placeholder="$ amount" />
              <button data-testid="refi-extra-add" onClick={addExtra} className="inline-flex items-center gap-1 rounded-none bg-black text-white border border-black h-9 px-3 mono text-[10px] uppercase tracking-[0.15em]"><Check size={12} /> Add</button>
            </div>
            {extraRows.length > 0 && <div className="mt-2 space-y-1">
              {extraRows.map((r, i) => (
                <div key={i} className="flex items-center justify-between mono text-[11px]">
                  <span>{r.investor_name} · {fmtUSD(parseFloat(r.amount) || 0)} <span className="text-muted-foreground">(new money)</span></span>
                  <button data-testid={`refi-extra-remove-${i}`} onClick={() => removeExtra(i)} className="text-muted-foreground hover:text-destructive"><X size={12} /></button>
                </div>
              ))}
            </div>}
          </div>
          <div>
            <div className="flex items-center justify-between mb-2 gap-2">
              <div className="mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Existing group — split of remainder ({fmtUSD(remainder)})</div>
              <div className="flex items-center gap-2">
                <button data-testid="refi-fill-active" onClick={fillFromActive} className="mono text-[10px] uppercase tracking-[0.15em] border border-black bg-black text-white px-3 h-8 hover:bg-neutral-800 transition-colors">Fill from active balance</button>
                <button data-testid="refi-fill-proportional" onClick={fillProportional} className="mono text-[10px] uppercase tracking-[0.15em] border border-black px-3 h-8 hover:bg-black hover:text-white transition-colors">Fill proportional</button>
              </div>
            </div>
            <label className="flex items-center gap-2 mono text-[10px] mb-2"><input data-testid="refi-reinvest" type="checkbox" checked={reinvest} onChange={(e) => setReinvest(e.target.checked)} /> Existing group is reinvesting prior payouts — netted from their Active Balance so the reinvested cash isn't counted twice (they still earn a full return on it)</label>
            <div className="space-y-2 max-h-56 overflow-y-auto">
              {rows.length === 0 && <div className="mono text-[11px] text-muted-foreground">No investors on the parent deal. Add capital and they can be added after.</div>}
              {rows.map((r, i) => {
                const amt = parseFloat(r.amount) || 0;
                const over = reinvest && amt > (r.active_balance || 0) + 0.5;
                return (
                <div key={r.investor_id} className="flex items-center gap-3">
                  <div className="flex-1 mono text-[11px] truncate">{r.investor_name || r.investor_id} <span className="text-muted-foreground">· paid to date {fmtUSD(r.active_balance || 0)}</span>{over && <span className="text-destructive"> · exceeds balance!</span>}</div>
                  <input data-testid={`refi-inv-amount-${i}`} value={r.amount} onChange={(e) => { const v = e.target.value.replace(/[^0-9.]/g, ""); setRows(rows.map((x, j) => j === i ? { ...x, amount: v } : x)); }} className={`rounded-none border bg-background h-9 px-2 mono text-[11px] w-32 text-right ${over ? "border-destructive text-destructive" : "border-black"}`} placeholder="0" />
                </div>
                );
              })}
            </div>
            {rows.length > 0 && <div className="mono text-[10px] text-muted-foreground mt-2">Allocated {fmtUSD(allocated)} of {fmtUSD(fundedTotal)}{Math.abs(allocated - fundedTotal) > 1 ? " — amounts don't match funded" : " ✓"}</div>}
          </div>
          <div className="flex gap-3 pt-2">
            <button data-testid="refi-submit" disabled={busy} onClick={submit} className="flex-1 h-11 bg-black text-white mono text-xs uppercase tracking-[0.2em] hover:bg-neutral-800 disabled:opacity-60">{busy ? "Creating…" : `Create ${kind === "refi" ? "Refi" : "Add-On"}`}</button>
            <button onClick={onClose} className="h-11 px-6 border border-black mono text-xs uppercase tracking-[0.2em] hover:bg-neutral-100">Cancel</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function DealsSection({ deals, reload }) {
  const [form, setForm] = useState(emptyDeal());
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [invByDeal, setInvByDeal] = useState({});
  const [editRow, setEditRow] = useState(null); // { invId, amount }
  const [investors, setInvestors] = useState([]);
  const [shareSel, setShareSel] = useState({});
  const [refiDeal, setRefiDeal] = useState(null);
  useEffect(() => { adminApi.get("/site/admin/investors").then(({ data }) => setInvestors(data.investors || [])).catch(() => {}); }, []);
  const invName = (iid) => { const m = investors.find((x) => x._id === iid); return m ? (m.name || m.email || iid) : iid; };
  const setShared = async (deal, ids) => {
    try { await adminApi.put(`/site/admin/deals/${deal._id}/shared`, { investor_ids: ids }); reload(); return true; }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); return false; }
  };
  const addShare = async (deal, iid) => {
    if (!iid) return;
    const ok = await setShared(deal, [...new Set([...(deal.shared_with || []), iid])]);
    if (ok) { toast.success("Investor added — they'll see this deal in their portal"); setShareSel((s) => ({ ...s, [deal._id]: "" })); }
  };
  const removeShare = async (deal, iid) => {
    const ok = await setShared(deal, (deal.shared_with || []).filter((x) => x !== iid));
    if (ok) toast.success("Investor removed");
  };

  const loadDealInv = (id) => adminApi.get(`/site/admin/deals/${id}/investments`).then(({ data }) => setInvByDeal((m) => ({ ...m, [id]: data.investments }))).catch(() => {});
  const toggleExpand = (id) => { setExpanded((cur) => (cur === id ? null : id)); if (!invByDeal[id]) loadDealInv(id); };
  const exportDeal = (id) => window.open(`${API}/site/admin/deals/${id}/export.csv?token=${encodeURIComponent(localStorage.getItem("admin_token") || "")}`, "_blank");

  const startEdit = (d) => {
    setEditingId(d._id);
    setForm({ name: d.name, goal: String(d.goal), return_pct: String(d.return_pct), term_days: String(d.term_days), description: d.description || "", status: d.status });
    setShowForm(true);
  };
  const reset = () => { setForm(emptyDeal()); setEditingId(null); setShowForm(false); };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Enter a deal name");
    const payload = {
      name: form.name.trim(),
      goal: parseInt(form.goal || "0", 10) || 0,
      return_pct: parseFloat(form.return_pct || "0") || 0,
      term_days: parseInt(form.term_days || "0", 10) || 0,
      description: form.description,
      status: form.status,
    };
    try {
      if (editingId) await adminApi.patch(`/site/admin/deals/${editingId}`, payload);
      else await adminApi.post("/site/admin/deals", payload);
      toast.success(editingId ? "Deal updated" : "Deal created");
      reset(); reload();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const archive = async (id, val) => {
    try { await adminApi.post(`/site/admin/deals/${id}/${val ? "archive" : "unarchive"}`); toast.success(val ? "Deal archived" : "Deal unarchived"); reload(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const sendAgreements = async (id) => {
    if (!window.confirm("Email the Participation Agreement to every investor in this deal?")) return;
    try {
      const { data } = await adminApi.post(`/site/admin/deals/${id}/send-agreements`, { base_url: window.location.origin });
      toast.success(`Sent to ${data.sent} investor(s)` + (data.missing_email?.length ? ` · ${data.missing_email.length} missing an email` : ""));
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const setInvStatus = async (dealId, invId, status) => {
    setInvByDeal((m) => ({ ...m, [dealId]: (m[dealId] || []).map((r) => (r._id === invId ? { ...r, status } : r)) }));
    try {
      await adminApi.patch(`/site/admin/investments/${invId}`, { status });
      reload();
      toast.success("Wire status updated");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); loadDealInv(dealId); }
  };

  const saveDealInv = async (dealId, invId) => {
    const amt = parseInt(String(editRow?.amount).replace(/[^0-9]/g, ""), 10);
    if (!amt || amt <= 0) return toast.error("Enter an amount");
    const payload = { amount: amt };
    if (editRow?.pct !== "" && editRow?.pct != null) {
      const p = parseFloat(editRow.pct);
      if (isNaN(p) || p < 0) return toast.error("Enter a valid return %");
      payload.return_pct = p;
    }
    try { await adminApi.patch(`/site/admin/investments/${invId}`, payload); setEditRow(null); await loadDealInv(dealId); reload(); toast.success("Investor updated"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const deleteDealInv = async (dealId, invId) => {
    if (!window.confirm("Delete this contribution from the deal? This cannot be undone.")) return;
    try { await adminApi.delete(`/site/admin/investments/${invId}`); await loadDealInv(dealId); reload(); toast.success("Contribution deleted"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const toggleReinvested = async (dealId, invId, next) => {
    setInvByDeal((m) => ({ ...m, [dealId]: (m[dealId] || []).map((r) => (r._id === invId ? { ...r, reinvested: next } : r)) }));
    try {
      await adminApi.patch(`/site/admin/investments/${invId}`, { reinvested: next });
      reload();
      toast.success(next ? "Marked as reinvested — netted from active balance" : "Unmarked — counts as new money");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); loadDealInv(dealId); }
  };
  const resetReinvested = async (dealId) => {
    if (!window.confirm("Set every reinvested position's amount to that investor's true active balance as of this deal's funding date? This recomputes their reinvested amounts and returns.")) return;
    try {
      const { data } = await adminApi.post(`/site/admin/deals/${dealId}/reset-reinvested`);
      await loadDealInv(dealId); reload();
      toast.success(`Updated ${data.updated} reinvested position(s) to active balance at funding${(data.skipped || []).length ? ` · skipped ${data.skipped.length} not in parent` : ""}`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const feature = async (id) => {
    try { await adminApi.post(`/site/admin/deals/${id}/feature`); toast.success("Set as featured deal"); reload(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const duplicate = async (id) => {
    if (!window.confirm("Duplicate this deal? All settings, schedule and deck are copied — with NO investors — so you can add fresh investors and amounts.")) return;
    try { const { data } = await adminApi.post(`/site/admin/deals/${id}/duplicate`); toast.success(`Created "${data.name}" — now add investors and amounts`); reload(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const restoreDeal = async (id) => {
    try { await adminApi.post(`/site/admin/deals/${id}/restore`); reload(); toast.success("Deal restored"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const deleteDeal = async (id, name) => {
    if (!window.confirm(`Delete "${name}"? It's hidden from everywhere but can be restored with Undo.`)) return;
    try { await adminApi.delete(`/site/admin/deals/${id}`); reload(); toast.success("Deal deleted", { action: { label: "Undo", onClick: () => restoreDeal(id) } }); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div data-testid="admin-deals">
      <div className="flex items-center justify-between pb-3 border-b border-black">
        <h1 className="text-3xl sm:text-4xl font-black tracking-tighter">Deals</h1>
        <Button data-testid="deal-add-btn" onClick={() => (showForm ? reset() : setShowForm(true))} className="rounded-none bg-black text-white hover:bg-white hover:text-black border border-black transition-colors mono text-xs uppercase tracking-[0.2em] h-10">
          {showForm ? "Cancel" : "+ New Deal"}
        </Button>
      </div>

      {showForm && (
        <form onSubmit={save} data-testid="deal-form" className="border border-black p-6 mt-6 grid sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <Label className="mono text-xs uppercase tracking-[0.2em]">Deal Name</Label>
            <Input data-testid="deal-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={`${field} mt-2`} placeholder="Company Y AR Financing" />
          </div>
          <div>
            <Label className="mono text-xs uppercase tracking-[0.2em]">Goal ($)</Label>
            <Input data-testid="deal-goal" inputMode="numeric" value={form.goal} onChange={(e) => setForm({ ...form, goal: e.target.value.replace(/[^0-9]/g, "") })} className={`${field} mt-2`} placeholder="2000000" />
          </div>
          <div>
            <Label className="mono text-xs uppercase tracking-[0.2em]">Return (%)</Label>
            <Input data-testid="deal-return" inputMode="decimal" value={form.return_pct} onChange={(e) => setForm({ ...form, return_pct: e.target.value.replace(/[^0-9.]/g, "") })} className={`${field} mt-2`} placeholder="30" />
          </div>
          <div>
            <Label className="mono text-xs uppercase tracking-[0.2em]">Term (days)</Label>
            <Input data-testid="deal-term" inputMode="numeric" value={form.term_days} onChange={(e) => setForm({ ...form, term_days: e.target.value.replace(/[^0-9]/g, "") })} className={`${field} mt-2`} placeholder="65" />
          </div>
          <div>
            <Label className="mono text-xs uppercase tracking-[0.2em]">Status</Label>
            <select data-testid="deal-status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="w-full rounded-none border border-black bg-background h-11 px-2 mt-2 mono text-[11px] uppercase tracking-[0.1em] focus:outline-none">
              <option value="open">Open</option>
              <option value="closed">Closed</option>
            </select>
          </div>
          <div className="sm:col-span-2">
            <Label className="mono text-xs uppercase tracking-[0.2em]">Description</Label>
            <textarea data-testid="deal-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} className="w-full rounded-none border border-black bg-background p-3 mt-2 text-sm focus:outline-none focus:border-black" />
          </div>
          <div className="sm:col-span-2">
            <Button type="submit" data-testid="deal-save" className="rounded-none bg-black text-white hover:bg-white hover:text-black border border-black transition-colors h-11 px-8 mono text-xs uppercase tracking-[0.2em]">
              {editingId ? "Save Deal" : "Create Deal"}
            </Button>
          </div>
        </form>
      )}

      <div className="mt-8 grid gap-4">
        {deals.length === 0 && <div className="border border-black border-dashed p-16 text-center mono text-xs uppercase tracking-[0.2em] text-muted-foreground">No deals yet.</div>}
        {deals.map((d) => {
          const open = expanded === d._id;
          const rows = invByDeal[d._id] || [];
          const dealTotal = rows.reduce((s, x) => s + (x.amount || 0), 0);
          return (
          <div key={d._id} data-testid={`deal-row-${d._id}`} className="border border-black">
            <div className="flex items-start justify-between flex-wrap gap-4 p-6">
              <button type="button" data-testid={`deal-toggle-${d._id}`} onClick={() => toggleExpand(d._id)} className="flex items-start gap-3 text-left">
                <ChevronDown size={20} className={`mt-1 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
                <div>
                  <div className="flex items-center gap-3 flex-wrap">
                    <h3 className="text-xl font-semibold tracking-tight">{d.name}</h3>
                    {d.featured && <span className="mono text-[9px] uppercase tracking-[0.2em] bg-black text-white px-2 py-0.5 inline-flex items-center gap-1"><Star size={10} /> Featured</span>}
                    <span className={`mono text-[9px] uppercase tracking-[0.2em] px-2 py-0.5 border ${d.status === "closed" ? "bg-black text-white border-black" : "border-black"}`}>{d.status}</span>
                    {d.archived && <span className="mono text-[9px] uppercase tracking-[0.2em] px-2 py-0.5 border border-muted-foreground text-muted-foreground">Archived</span>}
                  </div>
                  <div className="mono text-xs text-muted-foreground mt-2">
                    {d.return_pct}% return · {d.term_days} days · {d.investment_count} investment(s)
                  </div>
                  <div className="mt-3 flex items-baseline gap-3">
                    <span className="text-2xl font-black tracking-tighter">{fmtUSD(d.committed)}</span>
                    <span className="mono text-xs text-muted-foreground">/ {fmtUSD(d.goal)} goal</span>
                  </div>
                </div>
              </button>
              <div className="flex items-center gap-3">
                {!d.featured && !d.archived && <button data-testid={`deal-feature-${d._id}`} onClick={() => feature(d._id)} className="mono text-[10px] uppercase tracking-[0.15em] hover:text-muted-foreground inline-flex items-center gap-1"><Star size={12} /> Feature</button>}
                <button data-testid={`deal-edit-${d._id}`} onClick={() => startEdit(d)} className="mono text-[10px] uppercase tracking-[0.15em] hover:text-muted-foreground inline-flex items-center gap-1"><Pencil size={12} /> Edit</button>
                <button data-testid={`deal-duplicate-${d._id}`} onClick={() => duplicate(d._id)} className="mono text-[10px] uppercase tracking-[0.15em] hover:text-muted-foreground inline-flex items-center gap-1"><Copy size={12} /> Duplicate</button>
                <button data-testid={`deal-refi-${d._id}`} onClick={() => setRefiDeal(d)} className="mono text-[10px] uppercase tracking-[0.15em] hover:text-muted-foreground inline-flex items-center gap-1"><FileSignature size={12} /> Add-On / Refi</button>
                {refiDeal && refiDeal._id === d._id && <RefiModal deal={d} investors={investors} onClose={() => setRefiDeal(null)} onDone={reload} />}
                {d.archived
                  ? <button data-testid={`deal-unarchive-${d._id}`} onClick={() => archive(d._id, false)} className="mono text-[10px] uppercase tracking-[0.15em] hover:text-muted-foreground inline-flex items-center gap-1"><ArchiveRestore size={12} /> Unarchive</button>
                  : <button data-testid={`deal-archive-${d._id}`} onClick={() => archive(d._id, true)} className="mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground hover:text-black inline-flex items-center gap-1"><Archive size={12} /> Archive</button>}
                <button data-testid={`deal-delete-${d._id}`} onClick={() => deleteDeal(d._id, d.name)} className="mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground hover:text-destructive inline-flex items-center gap-1"><Trash2 size={12} /> Delete</button>
              </div>
            </div>

            {open && (
              <div data-testid={`deal-investors-${d._id}`} className="border-t border-black">
                <div className="flex items-center justify-between gap-3 p-4 bg-muted flex-wrap">
                  <span className="mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">Investors in this deal ({rows.length})</span>
                  <div className="flex items-center gap-2">
                    <button data-testid={`deal-send-agreement-${d._id}`} onClick={() => sendAgreements(d._id)} className="inline-flex items-center gap-2 rounded-none bg-black text-white hover:bg-white hover:text-black border border-black h-8 px-3 mono text-[10px] uppercase tracking-[0.15em] transition-colors">
                      <FileSignature size={12} /> Send Agreement
                    </button>
                    {(d.relation || d.parent_deal_id) && (rows.some((r) => r.reinvested)) && (
                      <button data-testid={`deal-reset-reinvested-${d._id}`} onClick={() => resetReinvested(d._id)} className="inline-flex items-center gap-2 rounded-none border border-black h-8 px-3 mono text-[10px] uppercase tracking-[0.15em] hover:bg-black hover:text-white transition-colors">
                        <Repeat size={12} /> Set to active balance at funding
                      </button>
                    )}
                    <button data-testid={`deal-export-${d._id}`} onClick={() => exportDeal(d._id)} className="inline-flex items-center gap-2 rounded-none border border-black h-8 px-3 mono text-[10px] uppercase tracking-[0.15em] hover:bg-black hover:text-white transition-colors">
                      <Download size={12} /> Download Sheet
                    </button>
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-y border-border mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                        <th className="text-left p-3 font-normal">Investor</th>
                        <th className="text-right p-3 font-normal">Amount</th>
                        <th className="text-left p-3 font-normal">Date Invested</th>
                        <th className="text-right p-3 font-normal">Return %</th>
                        <th className="text-right p-3 font-normal">Return</th>
                        <th className="text-right p-3 font-normal">% of Deal</th>
                        <th className="text-left p-3 font-normal">Wire Status</th>
                        <th className="text-right p-3 font-normal">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.length === 0 && <tr><td colSpan={8} className="p-4 text-center mono text-xs text-muted-foreground">No investors in this deal yet.</td></tr>}
                      {rows.map((r) => {
                        const editing = editRow?.invId === r._id;
                        return (
                        <tr key={r._id} data-testid={`deal-inv-${r._id}`} className={`border-b border-border last:border-0 ${STATUS_ROW[r.status] || ""}`}>
                          <td className="p-3">
                            <div className="font-medium">{r.investor_name}</div>
                            {r.email && <div className="mono text-[11px] text-muted-foreground">{r.email}</div>}
                            <label data-testid={`deal-inv-reinvested-label-${r._id}`} className="mt-1 inline-flex items-center gap-1.5 mono text-[10px] uppercase tracking-[0.12em] text-muted-foreground cursor-pointer select-none">
                              <input data-testid={`deal-inv-reinvested-${r._id}`} type="checkbox" checked={!!r.reinvested} onChange={(e) => toggleReinvested(d._id, r._id, e.target.checked)} className="accent-black" />
                              Reinvested {r.reinvested && <span className="text-black normal-case tracking-normal">· netted from active balance</span>}
                            </label>
                          </td>
                          <td className="p-3 text-right font-semibold">
                            {editing
                              ? <Input data-testid={`deal-inv-amount-input-${r._id}`} inputMode="numeric" value={editRow.amount} onChange={(e) => setEditRow({ ...editRow, amount: e.target.value.replace(/[^0-9]/g, "") })} className="rounded-none border-black h-8 text-right w-28 ml-auto focus-visible:ring-0 focus-visible:border-black" />
                              : fmtUSD(r.amount)}
                          </td>
                          <td className="p-3 mono text-xs text-muted-foreground">{(r.created_at || "").slice(0, 10)}</td>
                          <td className="p-3 text-right">
                            {editing
                              ? <div className="flex items-center justify-end gap-1"><Input data-testid={`deal-inv-pct-input-${r._id}`} inputMode="decimal" value={editRow.pct} onChange={(e) => setEditRow({ ...editRow, pct: e.target.value.replace(/[^0-9.]/g, "") })} className="rounded-none border-black h-8 text-right w-16 focus-visible:ring-0 focus-visible:border-black" /><span className="mono text-xs">%</span></div>
                              : <span className="mono text-xs font-medium">{(r.return_pct ?? 0)}%</span>}
                          </td>
                          <td className="p-3 text-right" data-testid={`deal-inv-return-${r._id}`}>
                            {editing
                              ? fmtUSD(Math.round((parseInt(String(editRow.amount).replace(/[^0-9]/g, ""), 10) || 0) * (1 + (parseFloat(editRow.pct) || 0) / 100)))
                              : fmtUSD(r.return_amount)}
                          </td>
                          <td className="p-3 text-right font-medium">{dealTotal ? ((r.amount / dealTotal) * 100).toFixed(1) : "0.0"}%</td>
                          <td className="p-3">
                            <select data-testid={`deal-inv-status-${r._id}`} value={r.status} onChange={(e) => setInvStatus(d._id, r._id, e.target.value)} className="rounded-none border border-black bg-background h-8 px-2 mono text-[10px] uppercase focus:outline-none">
                              {STATUS_OPTS.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
                            </select>
                          </td>
                          <td className="p-3">
                            <div className="flex items-center justify-end gap-3">
                              {editing ? (
                                <>
                                  <button data-testid={`deal-inv-save-${r._id}`} onClick={() => saveDealInv(d._id, r._id)} className="mono text-[10px] uppercase tracking-[0.15em] hover:text-muted-foreground">Save</button>
                                  <button data-testid={`deal-inv-cancel-${r._id}`} onClick={() => setEditRow(null)} className="mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground hover:text-black">Cancel</button>
                                </>
                              ) : (
                                <>
                                  <button data-testid={`deal-inv-edit-${r._id}`} onClick={() => setEditRow({ invId: r._id, amount: String(r.amount), pct: String(r.return_pct ?? "") })} className="mono text-[10px] uppercase tracking-[0.15em] hover:text-muted-foreground">Edit</button>
                                  <button data-testid={`deal-inv-delete-${r._id}`} onClick={() => deleteDealInv(d._id, r._id)} className="mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground hover:text-destructive">Delete</button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <div data-testid={`deal-share-${d._id}`} className="border border-black p-4 mb-6">
                  <div className="mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-3">Share with investors — they'll see this deal in their portal on login</div>
                  <div className="flex flex-wrap items-center gap-3">
                    <select data-testid={`deal-share-select-${d._id}`} value={shareSel[d._id] || ""} onChange={(e) => setShareSel((s) => ({ ...s, [d._id]: e.target.value }))} className="rounded-none border border-black bg-background h-10 px-2 mono text-[11px] focus:outline-none min-w-[240px]">
                      <option value="">Select an investor…</option>
                      {investors.filter((x) => !(d.shared_with || []).includes(x._id)).map((x) => (
                        <option key={x._id} value={x._id}>{x.name || x.email || x._id}{x.email ? ` · ${x.email}` : ""}</option>
                      ))}
                    </select>
                    <button data-testid={`deal-share-add-${d._id}`} onClick={() => addShare(d, shareSel[d._id])} className="inline-flex items-center gap-2 rounded-none bg-black text-white hover:bg-white hover:text-black border border-black transition-colors h-10 px-4 mono text-[10px] uppercase tracking-[0.15em]">
                      <Check size={13} /> Add
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-3" data-testid={`deal-shared-list-${d._id}`}>
                    {(d.shared_with || []).length === 0 && <span className="mono text-[10px] text-muted-foreground">Not shared with anyone yet.</span>}
                    {(d.shared_with || []).map((iid) => (
                      <span key={iid} className="inline-flex items-center gap-2 border border-black px-3 h-8 mono text-[10px]">
                        {invName(iid)}
                        <button data-testid={`deal-share-remove-${d._id}-${iid}`} onClick={() => removeShare(d, iid)} className="text-muted-foreground hover:text-destructive"><X size={12} /></button>
                      </span>
                    ))}
                  </div>
                </div>
                <DealSchedule dealId={d._id} committed={d.committed} onChange={reload} />
                <DealDetails dealId={d._id} />
                <DeckEditor dealId={d._id} />
                <DocVault dealId={d._id} title="Deal Documents" />
              </div>
            )}
          </div>
          );
        })}
      </div>
    </div>
  );
}
/* ---------------- PAYOUTS ---------------- */
const PAYOUT_ROW = { requested: "bg-yellow-100", paid: "bg-green-100", declined: "bg-neutral-100" };
function PayoutsSection() {
  const [payouts, setPayouts] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [investors, setInvestors] = useState([]);
  const [deals, setDeals] = useState([]);
  const [logging, setLogging] = useState(false);
  const blankForm = { investor_id: "", amount: "", status: "paid", date: "", deal_id: "", note: "" };
  const [form, setForm] = useState(blankForm);

  const load = () => adminApi.get("/site/admin/payouts").then(({ data }) => setPayouts(data.payouts)).catch(() => {});
  useEffect(() => {
    load();
    adminApi.get("/site/admin/investors").then(({ data }) => setInvestors(data.investors || [])).catch(() => {});
    adminApi.get("/site/admin/deals").then(({ data }) => setDeals(data.deals || [])).catch(() => {});
  }, []);

  const setStatus = async (id, status) => {
    try { await adminApi.post(`/site/admin/payouts/${id}/status`, { status }); toast.success(`Marked ${status}`); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this payout request?")) return;
    try { await adminApi.delete(`/site/admin/payouts/${id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const saveLog = async () => {
    const amt = parseFloat(String(form.amount).replace(/[^0-9.]/g, ""));
    if (!form.investor_id) return toast.error("Pick an investor");
    if (!amt || amt <= 0) return toast.error("Enter an amount");
    try {
      await adminApi.post("/site/admin/payouts", { investor_id: form.investor_id, amount: amt, status: form.status, date: form.date || null, deal_id: form.deal_id || null, note: form.note });
      toast.success("Payout logged");
      setForm(blankForm); setLogging(false); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const pending = payouts.filter((p) => p.status === "requested").length;

  return (
    <div data-testid="admin-payouts">
      <div className="flex items-center justify-between pb-3 border-b border-black flex-wrap gap-3">
        <h1 className="text-3xl sm:text-4xl font-black tracking-tighter">Payouts</h1>
        <div className="flex items-center gap-4">
          <span className="mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{pending} pending · {payouts.length} total</span>
          <button data-testid="payout-log-btn" onClick={() => setLogging((v) => !v)} className="inline-flex items-center gap-2 bg-black text-white h-10 px-4 mono text-[10px] uppercase tracking-[0.2em] hover:bg-neutral-800">
            <Banknote size={14} /> {logging ? "Close" : "Log Payout"}
          </button>
        </div>
      </div>

      {logging && (
        <div data-testid="payout-log-form" className="border border-black p-5 mt-6 bg-neutral-50">
          <div className="mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-1">Log a withdrawal / override</div>
          <p className="mono text-[11px] text-muted-foreground mb-4 max-w-xl">Record funds an investor actually pulled out. This posts to their portal and reduces their available balance — it bypasses the usual eligibility rules.</p>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="mono text-[10px] uppercase tracking-[0.2em]">Investor</Label>
              <select data-testid="payout-log-investor" value={form.investor_id} onChange={(e) => setForm({ ...form, investor_id: e.target.value })} className="w-full rounded-none border border-black bg-background h-11 px-3 mt-2 text-sm">
                <option value="">Select investor…</option>
                {investors.map((i) => <option key={i._id} value={i._id}>{i.name} · {i.email}</option>)}
              </select>
            </div>
            <div>
              <Label className="mono text-[10px] uppercase tracking-[0.2em]">Amount ($)</Label>
              <Input data-testid="payout-log-amount" inputMode="decimal" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value.replace(/[^0-9.]/g, "") })} placeholder="0.00" className={`${field} mt-2`} />
            </div>
            <div>
              <Label className="mono text-[10px] uppercase tracking-[0.2em]">Status</Label>
              <select data-testid="payout-log-status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="w-full rounded-none border border-black bg-background h-11 px-3 mt-2 text-sm">
                <option value="paid">Paid (pulled out)</option>
                <option value="requested">Requested (pending)</option>
              </select>
            </div>
            <div>
              <Label className="mono text-[10px] uppercase tracking-[0.2em]">Date pulled</Label>
              <Input data-testid="payout-log-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className={`${field} mt-2`} />
            </div>
            <div>
              <Label className="mono text-[10px] uppercase tracking-[0.2em]">Deal (optional)</Label>
              <select data-testid="payout-log-deal" value={form.deal_id} onChange={(e) => setForm({ ...form, deal_id: e.target.value })} className="w-full rounded-none border border-black bg-background h-11 px-3 mt-2 text-sm">
                <option value="">No specific deal</option>
                {deals.map((d) => <option key={d._id} value={d._id}>{d.name}</option>)}
              </select>
            </div>
            <div>
              <Label className="mono text-[10px] uppercase tracking-[0.2em]">Note (optional)</Label>
              <Input data-testid="payout-log-note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="e.g. wired back early" className={`${field} mt-2`} />
            </div>
          </div>
          <div className="flex gap-3 mt-4">
            <button data-testid="payout-log-save" onClick={saveLog} className="bg-black text-white h-11 px-8 mono text-[10px] uppercase tracking-[0.2em] hover:bg-neutral-800">Save Payout</button>
            <button onClick={() => { setForm(blankForm); setLogging(false); }} className="h-11 px-4 mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Cancel</button>
          </div>
        </div>
      )}

      {payouts.length === 0 ? (
        <div className="border border-black border-dashed p-16 text-center mono text-xs uppercase tracking-[0.2em] text-muted-foreground mt-8">No payout requests yet.</div>
      ) : (
        <div className="space-y-3 mt-8">
          {payouts.map((p) => {
            const bank = p.bank || {};
            const open = openId === p._id;
            return (
              <div key={p._id} data-testid={`payout-row-${p._id}`} className={`border border-black ${PAYOUT_ROW[p.status] || ""}`}>
                <div className="flex items-center justify-between gap-4 p-4 flex-wrap">
                  <div className="min-w-0">
                    <div className="font-semibold">{p.investor_name} · {fmt2(p.amount)}{p.manual ? <span className="ml-2 border border-black px-1.5 py-0.5 mono text-[8px] uppercase tracking-[0.15em]">Logged</span> : null}</div>
                    <div className="mono text-[10px] text-muted-foreground">{p.email} · {(p.created_at || "").slice(0, 10)}{p.deal_name ? ` · ${p.deal_name}` : ""}{p.note ? ` · "${p.note}"` : ""}</div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="mono text-[9px] uppercase tracking-[0.2em] border border-black px-2 py-1">{p.status}</span>
                    <button data-testid={`payout-view-bank-${p._id}`} onClick={() => setOpenId(open ? null : p._id)} className="mono text-[10px] uppercase tracking-[0.15em] underline underline-offset-4 hover:text-black text-muted-foreground">
                      {open ? "Hide" : "Wire Details"}
                    </button>
                  </div>
                </div>
                {open && (
                  <div data-testid={`payout-bank-${p._id}`} className="border-t border-black bg-white p-4 grid sm:grid-cols-2 gap-3">
                    {[["Bank", bank.bank_name], ["Account Name", bank.account_name], ["Account #", bank.account_number], ["Routing #", bank.routing_number], ["SWIFT / BIC", bank.swift], ["Bank Address", bank.bank_address], ["Notes", bank.notes]].filter(([, v]) => v).map(([k, v]) => (
                      <div key={k}><div className="mono text-[9px] uppercase tracking-[0.2em] text-muted-foreground">{k}</div><div className="mono text-sm mt-0.5">{v}</div></div>
                    ))}
                    {!bank.account_number && !bank.bank_name && <div className="mono text-xs text-destructive">No bank details on file.</div>}
                  </div>
                )}
                <div className="border-t border-black bg-white p-3 flex items-center justify-end gap-3">
                  {p.status !== "paid" && <button data-testid={`payout-mark-paid-${p._id}`} onClick={() => setStatus(p._id, "paid")} className="inline-flex items-center gap-1 rounded-none bg-black text-white hover:bg-white hover:text-black border border-black transition-colors h-8 px-3 mono text-[10px] uppercase tracking-[0.15em]"><Check size={12} /> Mark Paid</button>}
                  {p.status === "requested" && <button data-testid={`payout-decline-${p._id}`} onClick={() => setStatus(p._id, "declined")} className="mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground hover:text-black">Decline</button>}
                  {p.status !== "requested" && <button onClick={() => setStatus(p._id, "requested")} className="mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground hover:text-black">Reopen</button>}
                  <button data-testid={`payout-delete-${p._id}`} onClick={() => remove(p._id)} className="mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground hover:text-destructive">Delete</button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}



/* ---------------- INVESTORS ---------------- */
function InvestorsSection({ onOpen }) {
  const [tab, setTab] = useState("investors"); // investors | waitlist | duplicates
  const [investors, setInvestors] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [dupes, setDupes] = useState([]);
  const [selected, setSelected] = useState({});
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ name: "", email: "" });

  const load = () => adminApi.get("/site/admin/investors").then(({ data }) => setInvestors(data.investors)).catch(() => {});
  const loadWaitlist = () => adminApi.get("/site/admin/waitlist").then(({ data }) => setWaitlist(data.waitlist)).catch(() => {});
  const loadDupes = () => adminApi.get("/site/admin/duplicate-investments").then(({ data }) => { setDupes(data.groups || []); setSelected({}); }).catch(() => {});
  useEffect(() => { load(); loadWaitlist(); }, []);
  useEffect(() => { if (tab === "duplicates") loadDupes(); }, [tab]);

  const dupeCount = dupes.reduce((n, g) => n + g.duplicates.length, 0);
  const selectedIds = Object.keys(selected).filter((id) => selected[id]);
  const deleteSelectedDupes = async () => {
    if (!selectedIds.length) return toast.error("Select at least one duplicate to remove");
    if (!window.confirm(`Permanently delete ${selectedIds.length} duplicate position(s)? This cannot be undone.`)) return;
    try {
      await Promise.all(selectedIds.map((id) => adminApi.delete(`/site/admin/investments/${id}`)));
      toast.success(`Removed ${selectedIds.length} duplicate(s)`);
      loadDupes(); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const add = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Enter a name");
    try { await adminApi.post("/site/admin/investors", { name: form.name.trim(), email: form.email.trim() }); toast.success("Investor added"); setForm({ name: "", email: "" }); setAdding(false); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const accept = async (id) => {
    try { await adminApi.post(`/site/admin/waitlist/${id}/accept`); toast.success("Moved to investors"); loadWaitlist(); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const removeWaitlist = async (id) => {
    if (!window.confirm("Remove this waitlist entry?")) return;
    try { await adminApi.delete(`/site/admin/waitlist/${id}`); toast.success("Removed"); loadWaitlist(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const removeInvestor = async (id) => {
    if (!window.confirm("Delete this investor and ALL of their investments? This cannot be undone.")) return;
    try { await adminApi.delete(`/site/admin/investors/${id}`); toast.success("Investor deleted"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const exportCsv = () => {
    const url = `${API}/site/admin/export.csv?token=${encodeURIComponent(localStorage.getItem("admin_token") || "")}`;
    window.open(url, "_blank");
  };

  return (
    <div data-testid="admin-investors">
      <div className="flex items-center justify-between pb-3 border-b border-black flex-wrap gap-3">
        <h1 className="text-3xl sm:text-4xl font-black tracking-tighter">Investors</h1>
        <div className="flex items-center gap-3">
          <button data-testid="export-csv-btn" onClick={exportCsv} className="inline-flex items-center gap-2 rounded-none border border-black h-10 px-4 mono text-xs uppercase tracking-[0.2em] hover:bg-black hover:text-white transition-colors">
            <Download size={14} /> Export CSV
          </button>
          <Button data-testid="investor-add-btn" onClick={() => setAdding((a) => !a)} className="rounded-none bg-black text-white hover:bg-white hover:text-black border border-black transition-colors mono text-xs uppercase tracking-[0.2em] h-10">
            {adding ? "Cancel" : "+ Add Investor"}
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-6 mt-4">
        <button data-testid="investors-subtab-investors" onClick={() => setTab("investors")} className={`py-2 mono text-[11px] uppercase tracking-[0.2em] border-b-2 -mb-px transition-colors ${tab === "investors" ? "border-black text-black" : "border-transparent text-muted-foreground hover:text-black"}`}>
          Active ({investors.length})
        </button>
        <button data-testid="investors-subtab-waitlist" onClick={() => setTab("waitlist")} className={`py-2 mono text-[11px] uppercase tracking-[0.2em] border-b-2 -mb-px transition-colors ${tab === "waitlist" ? "border-black text-black" : "border-transparent text-muted-foreground hover:text-black"}`}>
          Waitlist ({waitlist.length})
        </button>
        <button data-testid="investors-subtab-duplicates" onClick={() => setTab("duplicates")} className={`py-2 mono text-[11px] uppercase tracking-[0.2em] border-b-2 -mb-px transition-colors ${tab === "duplicates" ? "border-black text-black" : "border-transparent text-muted-foreground hover:text-black"}`}>
          Duplicates{dupeCount ? ` (${dupeCount})` : ""}
        </button>
      </div>

      {adding && tab === "investors" && (
        <form onSubmit={add} data-testid="investor-add-form" className="border border-black p-6 mt-6 grid sm:grid-cols-3 gap-4 items-end">
          <div>
            <Label className="mono text-xs uppercase tracking-[0.2em]">Name</Label>
            <Input data-testid="investor-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={`${field} mt-2`} placeholder="Full name" />
          </div>
          <div>
            <Label className="mono text-xs uppercase tracking-[0.2em]">Email</Label>
            <Input data-testid="investor-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={`${field} mt-2`} placeholder="investor@email.com" />
          </div>
          <Button type="submit" data-testid="investor-save" className="rounded-none bg-black text-white hover:bg-white hover:text-black border border-black transition-colors h-11 px-8 mono text-xs uppercase tracking-[0.2em]">Add</Button>
        </form>
      )}

      {tab === "investors" ? (
        investors.length === 0 ? (
          <div className="border border-black border-dashed p-16 text-center mono text-xs uppercase tracking-[0.2em] text-muted-foreground mt-8">No investors yet.</div>
        ) : (
          <div className="border border-black overflow-x-auto mt-8">
            <table className="w-full text-sm" data-testid="investors-table">
              <thead>
                <tr className="border-b border-black mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                  <th className="text-left p-4 font-normal">Investor</th>
                  <th className="text-left p-4 font-normal">Email</th>
                  <th className="text-right p-4 font-normal">Total Invested</th>
                  <th className="text-right p-4 font-normal">Investments</th>
                  <th className="text-right p-4 font-normal" />
                </tr>
              </thead>
              <tbody>
                {investors.map((inv) => (
                  <tr key={inv._id} data-testid={`investor-row-${inv._id}`} onClick={() => onOpen(inv._id)} className="border-b border-border last:border-0 cursor-pointer hover:bg-muted">
                    <td className="p-4 font-medium">{inv.name}</td>
                    <td className="p-4 mono text-xs text-muted-foreground">{inv.email || "—"}</td>
                    <td className="p-4 text-right font-semibold">{fmtUSD(inv.total_invested)}</td>
                    <td className="p-4 text-right">{inv.investment_count}</td>
                    <td className="p-4 text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-4">
                        <button data-testid={`investor-delete-${inv._id}`} onClick={() => removeInvestor(inv._id)} className="mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground hover:text-destructive">Delete</button>
                        <button data-testid={`investor-open-${inv._id}`} onClick={() => onOpen(inv._id)} className="text-muted-foreground hover:text-black"><ArrowRight size={15} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : tab === "waitlist" ? (
        waitlist.length === 0 ? (
          <div className="border border-black border-dashed p-16 text-center mono text-xs uppercase tracking-[0.2em] text-muted-foreground mt-8">No one on the waitlist yet.</div>
        ) : (
          <div className="border border-black overflow-x-auto mt-8">
            <table className="w-full text-sm" data-testid="waitlist-table">
              <thead>
                <tr className="border-b border-black mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                  <th className="text-left p-4 font-normal">Name</th>
                  <th className="text-left p-4 font-normal">Email</th>
                  <th className="text-left p-4 font-normal">Joined</th>
                  <th className="text-right p-4 font-normal">Actions</th>
                </tr>
              </thead>
              <tbody>
                {waitlist.map((w) => (
                  <tr key={w._id} data-testid={`waitlist-row-${w._id}`} className="border-b border-border last:border-0">
                    <td className="p-4 font-medium">{w.name}</td>
                    <td className="p-4 mono text-xs text-muted-foreground">{w.email || "—"}</td>
                    <td className="p-4 mono text-xs text-muted-foreground">{(w.created_at || "").slice(0, 10)}</td>
                    <td className="p-4 text-right">
                      <div className="flex items-center justify-end gap-3">
                        <button data-testid={`waitlist-accept-${w._id}`} onClick={() => accept(w._id)} className="inline-flex items-center gap-1 rounded-none bg-black text-white hover:bg-white hover:text-black border border-black transition-colors h-8 px-3 mono text-[10px] uppercase tracking-[0.15em]">
                          <Check size={12} /> Accept
                        </button>
                        <button data-testid={`waitlist-remove-${w._id}`} onClick={() => removeWaitlist(w._id)} className="mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground hover:text-destructive">Remove</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : (
        dupes.length === 0 ? (
          <div data-testid="dupes-empty" className="border border-black border-dashed p-16 text-center mono text-xs uppercase tracking-[0.2em] text-muted-foreground mt-8">No exact-duplicate positions found.</div>
        ) : (
          <div className="mt-8" data-testid="dupes-panel">
            <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
              <p className="mono text-[11px] text-muted-foreground max-w-xl">
                These are <span className="text-black font-semibold">exact duplicates</span> — same investor, deal, amount, return &amp; reinvest flag. The oldest row in each set is kept automatically. Tick the extras you want to remove.
              </p>
              <button data-testid="dupes-delete-selected" onClick={deleteSelectedDupes} disabled={!selectedIds.length} className="inline-flex items-center gap-2 rounded-none bg-black text-white h-10 px-4 mono text-[10px] uppercase tracking-[0.2em] hover:bg-neutral-800 disabled:opacity-40 disabled:cursor-not-allowed">
                <Trash2 size={13} /> Remove Selected{selectedIds.length ? ` (${selectedIds.length})` : ""}
              </button>
            </div>
            <div className="space-y-6">
              {dupes.map((g, gi) => (
                <div key={gi} data-testid={`dupe-group-${gi}`} className="border border-black">
                  <div className="border-b border-black p-4 flex items-center justify-between flex-wrap gap-2">
                    <div>
                      <span className="font-semibold">{g.investor_name || "Unknown"}</span>
                      <span className="mono text-[11px] text-muted-foreground"> · {g.deal_name || "—"}</span>
                    </div>
                    <div className="mono text-[11px]">
                      {fmtUSD(g.amount)} <span className="text-muted-foreground">invested · return {fmtUSD(g.return_amount)}</span>
                      {g.reinvested ? <span className="ml-2 border border-black px-2 py-0.5 text-[9px] uppercase tracking-[0.15em]">Reinvested</span> : null}
                    </div>
                  </div>
                  <table className="w-full text-sm">
                    <tbody>
                      <tr className="border-b border-black/10 bg-muted/40">
                        <td className="p-3 w-10" />
                        <td className="p-3 mono text-[11px] text-muted-foreground">Kept (oldest)</td>
                        <td className="p-3 mono text-[10px] text-muted-foreground text-right">{(g.keeper.created_at || "").slice(0, 10)}</td>
                      </tr>
                      {g.duplicates.map((d) => (
                        <tr key={d.id} data-testid={`dupe-row-${d.id}`} className="border-b border-black/10 last:border-0">
                          <td className="p-3 w-10">
                            <input type="checkbox" data-testid={`dupe-check-${d.id}`} checked={!!selected[d.id]} onChange={(e) => setSelected((s) => ({ ...s, [d.id]: e.target.checked }))} className="h-4 w-4 accent-black" />
                          </td>
                          <td className="p-3 mono text-[11px]">Duplicate <span className="text-muted-foreground">· {d.status || "—"}</span></td>
                          <td className="p-3 mono text-[10px] text-muted-foreground text-right">{(d.created_at || "").slice(0, 10)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
          </div>
        )
      )}
    </div>
  );
}

/* ---------------- INVESTOR DETAIL ---------------- */
function InvestorDetail({ id, deals, onBack, onChanged }) {
  const [data, setData] = useState(null);
  const [profile, setProfile] = useState({ name: "", email: "", notes: "" });
  const [bank, setBank] = useState({});
  const [savingProfile, setSavingProfile] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [showBank, setShowBank] = useState(false);
  const [invForm, setInvForm] = useState({ deal_id: "", amount: "", status: "pending" });
  const [editingInv, setEditingInv] = useState(null);
  const [docs, setDocs] = useState([]);
  const [sendDealId, setSendDealId] = useState("");

  const load = () => adminApi.get(`/site/admin/investors/${id}`).then(({ data }) => {
    setData(data);
    setProfile({ name: data.investor.name || "", email: data.investor.email || "", notes: data.investor.notes || "" });
    setBank(data.investor.bank || {});
  }).catch(() => {});
  const loadDocs = () => adminApi.get(`/site/admin/investors/${id}/documents`).then(({ data }) => setDocs(data.documents)).catch(() => {});
  useEffect(() => { load(); loadDocs(); }, [id]);

  const flagUnflaggedReinvest = async () => {
    const list = data?.stats?.unflagged_reinvest || [];
    if (!list.length) return;
    try {
      await Promise.all(list.map((u) => adminApi.patch(`/site/admin/investments/${u.investment_id}`, { reinvested: true })));
      toast.success(`Flagged ${list.length} position(s) as reinvested — now netted out`);
      load(); onChanged?.();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  useEffect(() => { if (deals.length && !invForm.deal_id) setInvForm((f) => ({ ...f, deal_id: deals[0]._id })); }, [deals]);

  const saveProfile = async () => {
    setSavingProfile(true);
    try { await adminApi.patch(`/site/admin/investors/${id}`, { name: profile.name, email: profile.email, notes: profile.notes, bank }); toast.success("Saved"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSavingProfile(false); }
  };

  const addInvestment = async (e) => {
    e.preventDefault();
    const amt = parseInt(String(invForm.amount).replace(/[^0-9]/g, ""), 10);
    if (!invForm.deal_id) return toast.error("Select a deal");
    if (!amt || amt <= 0) return toast.error("Enter an amount");
    try { await adminApi.post(`/site/admin/investors/${id}/investments`, { deal_id: invForm.deal_id, amount: amt, status: invForm.status }); toast.success("Investment added"); setInvForm({ deal_id: deals[0]?._id || "", amount: "", status: "pending" }); load(); onChanged?.(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const saveInvEdit = async (row) => {
    const amt = parseInt(String(row.amount).replace(/[^0-9]/g, ""), 10);
    if (!amt || amt <= 0) return toast.error("Enter an amount");
    try { await adminApi.patch(`/site/admin/investments/${row._id}`, { deal_id: row.deal_id, amount: amt, status: row.status }); toast.success("Updated"); setEditingInv(null); load(); onChanged?.(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const removeInv = async (invId) => {
    if (!window.confirm("Delete this investment?")) return;
    try { await adminApi.delete(`/site/admin/investments/${invId}`); toast.success("Deleted"); load(); onChanged?.(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const resendDoc = async (aid) => {
    try { await adminApi.post(`/site/admin/agreements/${aid}/resend`, { base_url: window.location.origin }); toast.success("Agreement resent"); loadDocs(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const deleteDoc = async (aid) => {
    if (!window.confirm("Delete this document?")) return;
    try { await adminApi.delete(`/site/admin/agreements/${aid}`); toast.success("Document deleted"); loadDocs(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const sendAgreement = async () => {
    const did = sendDealId || deals[0]?._id;
    if (!did) return toast.error("Create a deal first");
    if (!profile.email.trim()) return toast.error("Add an email for this investor first (save the profile)");
    try {
      await adminApi.post(`/site/admin/investors/${id}/send-agreement`, { deal_id: did, base_url: window.location.origin });
      toast.success("Participation Agreement sent");
      loadDocs();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const viewAsInvestor = async () => {
    try {
      const { data } = await adminApi.get(`/site/admin/investors/${id}/portal-token`);
      window.open(`/portal?as=${encodeURIComponent(data.token)}`, "_blank");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  if (!data) return <div className="mono text-xs text-muted-foreground animate-pulse">Loading…</div>;
  const total = data.investments.reduce((s, i) => s + (i.amount || 0), 0);
  const paidPct = data.stats?.projected_total ? Math.min(100, Math.max(0, (data.stats.active_balance / data.stats.projected_total) * 100)) : 0;
  const litSegs = paidPct > 0 ? Math.max(1, Math.round(paidPct / 5)) : 0;

  return (
    <div data-testid="investor-detail">
      <button data-testid="investor-detail-back" onClick={onBack} className="inline-flex items-center gap-2 mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground hover:text-black mb-6">
        <ArrowLeft size={13} /> All investors
      </button>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <h1 className="text-4xl font-black tracking-tighter">{profile.name || "Investor"}</h1>
        <button data-testid="view-as-investor" onClick={viewAsInvestor} className="inline-flex items-center gap-2 rounded-none border border-black h-10 px-4 mono text-[10px] uppercase tracking-[0.15em] hover:bg-black hover:text-white transition-colors">
          <Eye size={13} /> Sign in as investor
        </button>
      </div>
      <div className="mono text-[11px] text-muted-foreground mt-2">Portal is active automatically — this investor can sign in at /portal with their email and set their own login code.</div>

      <div data-testid="investor-stats" className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-6">
        <div data-testid="stat-active-balance" className="border-2 border-black bg-black text-white p-4">
          <div className="mono text-[9px] uppercase tracking-[0.2em] text-neutral-400">Active Balance</div>
          <div className="text-2xl font-black tracking-tighter mt-1">{fmt2(data.stats?.active_balance || 0)}</div>
          {(data.stats?.total_reinvested || 0) > 0 && <div className="mono text-[9px] text-amber-300 mt-1">net of {fmt2(data.stats.total_reinvested)} reinvested</div>}
        </div>
        <div data-testid="stat-total-invested" className="border border-black p-4">
          <div className="mono text-[9px] uppercase tracking-[0.2em] text-muted-foreground">Total Invested</div>
          <div className="text-2xl font-black tracking-tighter mt-1">{fmt2(data.stats?.original_capital ?? total)}</div>
          <div className="mono text-[9px] text-muted-foreground mt-1">{data.stats?.investment_count ?? data.investments.length} active position(s){(data.stats?.total_reinvested || 0) > 0 ? ` · +${fmt2(data.stats.total_reinvested)} reinvested` : ""}</div>
        </div>
        <div data-testid="stat-recent-deal" className="border border-black p-4">
          <div className="mono text-[9px] uppercase tracking-[0.2em] text-muted-foreground">Most Recent Deal</div>
          {data.stats?.recent_deal ? (
            <>
              <div className="text-base font-bold tracking-tight mt-1 truncate">{data.stats.recent_deal.deal_name || "—"}</div>
              <div className="mono text-[9px] text-muted-foreground mt-1">{fmt2(data.stats.recent_deal.amount)}{data.stats.recent_deal.date ? ` · ${data.stats.recent_deal.date}` : ""}{data.stats.recent_deal.reinvested ? " · reinvested" : ""}</div>
            </>
          ) : <div className="text-base font-bold mt-1 text-muted-foreground">No deals yet</div>}
        </div>
      </div>

      <div data-testid="investor-projected" className="mt-3 border-2 border-black p-4">
        <div className="mono text-[9px] uppercase tracking-[0.2em] text-muted-foreground">Paid out by all deals' end</div>
        <div className="flex flex-wrap gap-x-10 gap-y-3 mt-2">
          <div>
            <div className="mono text-[9px] uppercase tracking-[0.15em] text-muted-foreground">Principal returned</div>
            <div data-testid="projected-principal" className="text-xl font-black tracking-tighter mt-0.5">{fmt2(data.stats?.original_capital || 0)}</div>
          </div>
          <div>
            <div className="mono text-[9px] uppercase tracking-[0.15em] text-muted-foreground">Total profit</div>
            <div data-testid="projected-profit" className="text-xl font-black tracking-tighter mt-0.5">{fmt2(data.stats?.projected_profit || 0)}</div>
          </div>
          <div>
            <div className="mono text-[9px] uppercase tracking-[0.15em] text-muted-foreground">Total payout</div>
            <div data-testid="projected-total" className="text-xl font-black tracking-tighter mt-0.5">{fmt2(data.stats?.projected_total || 0)}</div>
          </div>
        </div>
        <div className="mt-4">
          <div className="flex items-center justify-between mono text-[9px] uppercase tracking-[0.15em] text-muted-foreground mb-1.5">
            <span>Percent paid in to final</span>
            <span data-testid="paid-pct" className="text-black font-black">{paidPct.toFixed(1)}%</span>
          </div>
          <div data-testid="paid-power-bar" className="flex gap-[3px] h-4">
            {Array.from({ length: 20 }).map((_, i) => (
              <div key={i} className={`flex-1 transition-colors ${i < litSegs ? "bg-black" : "bg-neutral-200"}`} />
            ))}
          </div>
          <div className="mono text-[9px] text-muted-foreground mt-1.5">{fmt2(data.stats?.active_balance || 0)} received of {fmt2(data.stats?.projected_total || 0)} total</div>
        </div>
      </div>

      <button data-testid="toggle-profile" onClick={() => setShowProfile((v) => !v)} className="w-full flex items-center justify-between text-xl font-black tracking-tighter mt-12 mb-4 pb-2 border-b border-black">
        <span>Profile</span>
        <ChevronDown size={20} className={`transition-transform ${showProfile ? "rotate-180" : ""}`} />
      </button>
      {showProfile && (
      <div className="grid sm:grid-cols-2 gap-4 max-w-2xl">
        <div>
          <Label className="mono text-xs uppercase tracking-[0.2em]">Name</Label>
          <Input data-testid="detail-name" value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} className={`${field} mt-2`} />
        </div>
        <div>
          <Label className="mono text-xs uppercase tracking-[0.2em]">Email</Label>
          <Input data-testid="detail-email" value={profile.email} onChange={(e) => setProfile({ ...profile, email: e.target.value })} className={`${field} mt-2`} />
        </div>
        <div className="sm:col-span-2">
          <Label className="mono text-xs uppercase tracking-[0.2em]">Notes</Label>
          <textarea data-testid="detail-notes" value={profile.notes} onChange={(e) => setProfile({ ...profile, notes: e.target.value })} rows={2} className="w-full rounded-none border border-black bg-background p-3 mt-2 text-sm focus:outline-none focus:border-black" />
        </div>
      </div>
      )}

      <button data-testid="toggle-banking" onClick={() => setShowBank((v) => !v)} className="w-full flex items-center justify-between text-xl font-black tracking-tighter mt-12 mb-2 pb-2 border-b border-black">
        <span>Banking (for payouts)</span>
        <ChevronDown size={20} className={`transition-transform ${showBank ? "rotate-180" : ""}`} />
      </button>
      {showBank && (<>
      <p className="mono text-xs text-muted-foreground mb-4 mt-2">The investor's own account — where returns/repayment are sent.</p>
      <div className="grid sm:grid-cols-2 gap-4 max-w-2xl">
        {BANK_FIELDS.map(([key, label]) => (
          <div key={key} className={key === "notes" || key === "bank_address" ? "sm:col-span-2" : ""}>
            <Label className="mono text-xs uppercase tracking-[0.2em]">{label}</Label>
            <Input data-testid={`bank-${key}`} value={bank[key] || ""} onChange={(e) => setBank({ ...bank, [key]: e.target.value })} className={`${field} mt-2`} />
          </div>
        ))}
      </div>
      </>)}
      <Button data-testid="detail-save" onClick={saveProfile} disabled={savingProfile} className="mt-6 rounded-none bg-black text-white hover:bg-white hover:text-black border border-black transition-colors h-12 px-10 mono text-xs uppercase tracking-[0.2em]">
        {savingProfile ? "Saving…" : "Save Profile & Banking"}
      </Button>

      {data.balances && data.balances.length > 0 && (
        <>
          <h2 className="text-xl font-black tracking-tighter mt-14 mb-2 pb-2 border-b border-black">Balances</h2>
          <div className="mono text-[10px] text-muted-foreground mb-4">Live balances update automatically each day based on each deal's payment schedule.</div>
          <div className="grid md:grid-cols-2 gap-4">
            {data.balances.map((b) => (
              <div key={b.investment_id} data-testid={`balance-${b.investment_id}`} className="border border-black p-4">
                <div className="flex items-center justify-between gap-2">
                  <div className="font-semibold">{b.deal_name}</div>
                  <div className="mono text-[10px] text-muted-foreground">{b.share_pct}% · {b.payments_made}/{b.payments_total} paid</div>
                </div>
                <div className="grid grid-cols-2 gap-3 mt-4">
                  <div><div className="mono text-[9px] uppercase tracking-[0.2em] text-muted-foreground">Started With</div><div className="text-lg font-black tracking-tighter">{fmt2(b.started_with)}</div></div>
                  <div><div className="mono text-[9px] uppercase tracking-[0.2em] text-muted-foreground">Current Value</div><div data-testid={`balance-value-${b.investment_id}`} className="text-lg font-black tracking-tighter">{fmt2(b.current_value)}</div></div>
                  <div><div className="mono text-[9px] uppercase tracking-[0.2em] text-muted-foreground">Received</div><div className="text-base font-bold text-green-700">{fmt2(b.received)}</div></div>
                  <div><div className="mono text-[9px] uppercase tracking-[0.2em] text-muted-foreground">Remaining Owed</div><div className="text-base font-bold">{fmt2(b.remaining)}</div></div>
                </div>
                <div className="mono text-[10px] text-muted-foreground mt-3">Total return {fmt2(b.total_return)} · {fmt2(b.per_payment)}/day{b.next_date ? ` · next ${b.next_date}` : " · complete"}</div>
              </div>
            ))}
          </div>
        </>
      )}
      <h2 className="text-xl font-black tracking-tighter mt-14 mb-4 pb-2 border-b border-black">Investments</h2>
      <div className="border border-black overflow-x-auto">
        <table className="w-full text-sm" data-testid="detail-investments-table">
          <thead>
            <tr className="border-b border-black mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
              <th className="text-left p-4 font-normal">Deal</th>
              <th className="text-right p-4 font-normal">Amount</th>
              <th className="text-right p-4 font-normal">Return</th>
              <th className="text-left p-4 font-normal">Status</th>
              <th className="text-right p-4 font-normal">Actions</th>
            </tr>
          </thead>
          <tbody>
            {data.investments.length === 0 && (
              <tr><td colSpan={5} className="p-6 text-center mono text-xs text-muted-foreground">No investments yet.</td></tr>
            )}
            {data.investments.map((row) => (
              <tr key={row._id} className={`border-b border-border last:border-0 ${editingInv?._id === row._id ? "" : STATUS_ROW[row.status] || ""}`}>
                {editingInv?._id === row._id ? (
                  <>
                    <td className="p-4">
                      <select data-testid={`inv-edit-deal-${row._id}`} value={editingInv.deal_id} onChange={(e) => setEditingInv({ ...editingInv, deal_id: e.target.value })} className="rounded-none border border-black bg-background h-9 px-2 mono text-[11px] focus:outline-none">
                        {deals.map((d) => <option key={d._id} value={d._id}>{d.name}</option>)}
                      </select>
                    </td>
                    <td className="p-4 text-right">
                      <Input data-testid={`inv-edit-amount-${row._id}`} inputMode="numeric" value={editingInv.amount} onChange={(e) => setEditingInv({ ...editingInv, amount: e.target.value.replace(/[^0-9]/g, "") })} className="rounded-none border-black h-9 text-right focus-visible:ring-0 focus-visible:border-black" />
                    </td>
                    <td className="p-4" />
                    <td className="p-4">
                      <select data-testid={`inv-edit-status-${row._id}`} value={editingInv.status} onChange={(e) => setEditingInv({ ...editingInv, status: e.target.value })} className="rounded-none border border-black bg-background h-9 px-2 mono text-[11px] uppercase focus:outline-none">
                        {STATUS_OPTS.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
                      </select>
                    </td>
                    <td className="p-4 text-right">
                      <div className="flex items-center justify-end gap-3">
                        <button data-testid={`inv-edit-save-${row._id}`} onClick={() => saveInvEdit(editingInv)} className="mono text-[10px] uppercase tracking-[0.15em] hover:text-muted-foreground">Save</button>
                        <button onClick={() => setEditingInv(null)} className="mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground hover:text-black">Cancel</button>
                      </div>
                    </td>
                  </>
                ) : (
                  <>
                    <td className="p-4 font-medium">{row.deal_name || "—"}</td>
                    <td className="p-4 text-right font-semibold">{fmtUSD(row.amount)}</td>
                    <td className="p-4 text-right">{fmtUSD(row.return_amount)}</td>
                    <td className="p-4"><span className="mono text-[10px] uppercase tracking-[0.15em] border border-black px-2 py-0.5">{STATUS_LABEL[row.status] || row.status}</span></td>
                    <td className="p-4 text-right">
                      <div className="flex items-center justify-end gap-3">
                        <button data-testid={`inv-edit-${row._id}`} onClick={() => setEditingInv({ _id: row._id, deal_id: row.deal_id, amount: String(row.amount), status: row.status })} className="mono text-[10px] uppercase tracking-[0.15em] hover:text-muted-foreground">Edit</button>
                        <button data-testid={`inv-delete-${row._id}`} onClick={() => removeInv(row._id)} className="mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground hover:text-destructive">Delete</button>
                      </div>
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <form onSubmit={addInvestment} data-testid="inv-add-form" className="border border-black p-6 mt-6 grid sm:grid-cols-4 gap-4 items-end">
        <div className="sm:col-span-2">
          <Label className="mono text-xs uppercase tracking-[0.2em]">Deal</Label>
          <select data-testid="inv-add-deal" value={invForm.deal_id} onChange={(e) => setInvForm({ ...invForm, deal_id: e.target.value })} className="w-full rounded-none border border-black bg-background h-11 px-2 mt-2 mono text-[11px] focus:outline-none">
            {deals.length === 0 && <option value="">No deals — create one first</option>}
            {deals.map((d) => <option key={d._id} value={d._id}>{d.name}</option>)}
          </select>
        </div>
        <div>
          <Label className="mono text-xs uppercase tracking-[0.2em]">Amount</Label>
          <Input data-testid="inv-add-amount" inputMode="numeric" value={invForm.amount} onChange={(e) => setInvForm({ ...invForm, amount: e.target.value.replace(/[^0-9]/g, "") })} className={`${field} mt-2`} placeholder="100000" />
        </div>
        <div>
          <Label className="mono text-xs uppercase tracking-[0.2em]">Status</Label>
          <select data-testid="inv-add-status" value={invForm.status} onChange={(e) => setInvForm({ ...invForm, status: e.target.value })} className="w-full rounded-none border border-black bg-background h-11 px-2 mt-2 mono text-[11px] uppercase focus:outline-none">
            {STATUS_OPTS.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
          </select>
        </div>
        <div className="sm:col-span-4">
          <Button type="submit" data-testid="inv-add-save" className="rounded-none bg-black text-white hover:bg-white hover:text-black border border-black transition-colors h-11 px-8 mono text-xs uppercase tracking-[0.2em]">+ Add Investment</Button>
        </div>
      </form>

      <h2 className="text-xl font-black tracking-tighter mt-14 mb-4 pb-2 border-b border-black">Documents</h2>
      <div className="border border-black p-4 mb-4 flex flex-col sm:flex-row sm:items-end gap-3" data-testid="send-agreement-control">
        <div className="flex-1">
          <Label className="mono text-xs uppercase tracking-[0.2em]">Send Participation Agreement for deal</Label>
          <select data-testid="send-agreement-deal" value={sendDealId} onChange={(e) => setSendDealId(e.target.value)} className="w-full rounded-none border border-black bg-background h-11 px-2 mt-2 mono text-[11px] focus:outline-none">
            {deals.length === 0 && <option value="">No deals yet</option>}
            {deals.map((d) => <option key={d._id} value={d._id}>{d.name}</option>)}
          </select>
        </div>
        <button type="button" data-testid="send-agreement-btn" onClick={sendAgreement} className="inline-flex items-center gap-2 rounded-none bg-black text-white hover:bg-white hover:text-black border border-black h-11 px-6 mono text-xs uppercase tracking-[0.2em] transition-colors">
          <FileSignature size={14} /> Send to {profile.name || "investor"}
        </button>
      </div>
      <div className="border border-black overflow-x-auto" data-testid="investor-documents">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-black mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
              <th className="text-left p-3 font-normal">Agreement</th>
              <th className="text-left p-3 font-normal">Status</th>
              <th className="text-left p-3 font-normal">Signed</th>
              <th className="text-right p-3 font-normal">Actions</th>
            </tr>
          </thead>
          <tbody>
            {docs.length === 0 && <tr><td colSpan={4} className="p-4 text-center mono text-xs text-muted-foreground">No agreements yet — use "Send Agreement" on the Deals tab.</td></tr>}
            {docs.map((doc) => (
              <tr key={doc._id} className="border-b border-border last:border-0">
                <td className="p-3 font-medium">Participation Agreement · {doc.deal_name}</td>
                <td className="p-3"><span className={`mono text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 border ${doc.status === "signed" ? "bg-black text-white border-black" : "border-black"}`}>{doc.status === "signed" ? "Executed" : "Sent"}</span></td>
                <td className="p-3 mono text-xs text-muted-foreground">{(doc.signed_at || "").slice(0, 10) || "—"}</td>
                <td className="p-3 text-right">
                  <div className="inline-flex items-center gap-3">
                    {doc.status === "signed"
                      ? <a data-testid={`doc-download-${doc._id}`} href={`${API}/sign/${doc.token}/document.pdf`} className="mono text-[10px] uppercase tracking-[0.15em] underline underline-offset-2 hover:text-muted-foreground">Download</a>
                      : <button data-testid={`doc-copylink-${doc._id}`} onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/sign/${doc.token}`); toast.success("Signing link copied"); }} className="mono text-[10px] uppercase tracking-[0.15em] underline underline-offset-2 hover:text-muted-foreground">Copy link</button>}
                    <button data-testid={`doc-resend-${doc._id}`} onClick={() => resendDoc(doc._id)} className="mono text-[10px] uppercase tracking-[0.15em] hover:text-muted-foreground">Resend</button>
                    <button data-testid={`doc-delete-${doc._id}`} onClick={() => deleteDoc(doc._id)} className="mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground hover:text-destructive">Delete</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ---------------- EMAIL ---------------- */
const BUILTIN_TEMPLATES = [
  { name: "Participation Agreement", subject: "Review & Sign — Participation Agreement", body: "Dear Investor,\n\nPlease review and sign your Participation Agreement for Company X AR Financing with Can I Have Money, LLC. Our side is already signed; you'll add your name, title, and signature, then download your fully executed copy.\n\nReview & sign here: [paste the investor's signing link]\n\nCanihavemoney LLC" },
  { name: "Wire Reminder", subject: "Reminder: Wire Instructions for Your Commitment", body: "Dear Investor,\n\nThank you for your commitment. As a reminder, your wire instructions are available in your investor portal. Please complete your wire at your earliest convenience.\n\nCanihavemoney LLC" },
  { name: "Thank You / Confirmation", subject: "Thank You — Your Commitment Is Confirmed", body: "Dear Investor,\n\nThank you for participating. Your commitment has been recorded. We will keep you updated as the facility progresses.\n\nWarm regards,\nCanihavemoney LLC" },
];

function DailyEmailPanel() {
  const [runs, setRuns] = useState(null);
  const [busy, setBusy] = useState(false);
  const [openRun, setOpenRun] = useState(0);
  const [settings, setSettings] = useState({ daily_enabled: true, weekly_enabled: true });

  const load = () => adminApi.get("/site/admin/email/log").then(({ data }) => setRuns(data.runs)).catch(() => setRuns([]));
  useEffect(() => {
    load();
    adminApi.get("/site/admin/email/settings").then(({ data }) => setSettings(data)).catch(() => {});
  }, []);

  const toggle = async (key) => {
    const next = { ...settings, [key]: !settings[key] };
    setSettings(next);
    try { await adminApi.put("/site/admin/email/settings", next); toast.success("Saved"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); setSettings(settings); }
  };

  const sendNow = async () => {
    setBusy(true);
    try {
      const { data } = await adminApi.post("/site/admin/email/daily-summary/run");
      toast.success(`Daily summary sent to ${data.sent} investor(s)`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const retryFailed = async (runId) => {
    setBusy(true);
    try {
      const { data } = await adminApi.post(`/site/admin/email/log/${runId}/retry`);
      toast.success(data.message || `Retried — ${data.sent} sent`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const fmtTime = (iso) => (iso ? new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "");
  const last = runs && runs[0];

  return (
    <div data-testid="daily-email-panel" className="max-w-2xl mt-6 border border-black">
      <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-black bg-black text-white flex-wrap">
        <div>
          <div className="mono text-[10px] uppercase tracking-[0.25em]">Daily Investor Summaries</div>
          <div className="mono text-[9px] tracking-[0.1em] text-neutral-400 mt-1">Auto-sends 8:00 AM ET · daily Mon–Thu, weekly summary Fri · auto-retries failures</div>
        </div>
        <button data-testid="daily-send-now" onClick={sendNow} disabled={busy} className="inline-flex items-center gap-2 bg-white text-black h-9 px-4 mono text-[10px] uppercase tracking-[0.15em] hover:bg-neutral-200 disabled:opacity-60">
          <Sparkles size={12} /> {busy ? "Sending…" : "Send Now"}
        </button>
      </div>

      <div className="flex flex-wrap gap-2 px-4 py-3 border-b border-neutral-200" data-testid="email-toggles">
        {[["daily_enabled", "Daily (Mon–Thu)", "toggle-daily"], ["weekly_enabled", "Weekly summary (Fri)", "toggle-weekly"]].map(([k, label, tid]) => (
          <button key={k} data-testid={tid} onClick={() => toggle(k)} className={`inline-flex items-center gap-2 h-9 px-3 mono text-[10px] uppercase tracking-[0.15em] border border-black transition-colors ${settings[k] ? "bg-black text-white" : "bg-white text-muted-foreground"}`}>
            <span className={`w-2.5 h-2.5 rounded-full ${settings[k] ? "bg-green-400" : "bg-neutral-400"}`} /> {label} {settings[k] ? "On" : "Off"}
          </button>
        ))}
      </div>

      <div className="px-4 py-3">
        {runs === null && <p className="mono text-[11px] text-muted-foreground animate-pulse">Loading…</p>}
        {runs && runs.length === 0 && (
          <p data-testid="daily-empty" className="mono text-[11px] text-muted-foreground">No daily emails have gone out yet. They'll send automatically at 8 AM ET on weekdays — or hit "Send Now" to test.</p>
        )}
        {last && (
          <div className="mono text-[11px] text-muted-foreground mb-3" data-testid="daily-last">
            Last run: <span className="text-black font-semibold">{fmtTime(last.run_at)}</span> · {last.sent} sent{last.skipped ? ` · ${last.skipped} skipped (no email)` : ""} · {last.trigger}
          </div>
        )}
        {runs && runs.map((r, i) => (
          <div key={r._id} className="border-t border-neutral-200 first:border-t-0">
            <button data-testid={`daily-run-${i}`} onClick={() => setOpenRun(openRun === i ? -1 : i)} className="w-full flex items-center justify-between gap-3 py-2 text-left">
              <span className="mono text-[11px]">{fmtTime(r.run_at)}</span>
              <span className="mono text-[10px] text-muted-foreground">{r.sent} sent{r.skipped ? ` · ${r.skipped} skipped` : ""} <ChevronDown size={12} className={`inline transition-transform ${openRun === i ? "rotate-180" : ""}`} /></span>
            </button>
            {openRun === i && (
              <div className="pb-3">
                {(() => { const nf = (r.details || []).filter((d) => d.status !== "sent" && d.email).length; return nf > 0 ? (
                  <button data-testid={`daily-retry-${i}`} onClick={() => retryFailed(r._id)} disabled={busy} className="mb-2 inline-flex items-center gap-2 h-8 px-3 bg-black text-white mono text-[10px] uppercase tracking-[0.15em] hover:bg-neutral-800 disabled:opacity-60">
                    Retry {nf} failed
                  </button>
                ) : null; })()}
                {(r.details || []).length === 0 && <p className="mono text-[10px] text-muted-foreground">No investors matched (deals need a schedule + investors with emails).</p>}
                {(r.details || []).map((d, j) => (
                  <div key={j} data-testid={`daily-detail-${i}-${j}`} className="flex items-center justify-between gap-2 py-1 border-b border-neutral-100 last:border-b-0">
                    <div className="min-w-0">
                      <span className="mono text-[11px]">{d.investor || "—"}</span>
                      <span className="mono text-[9px] text-muted-foreground ml-2">{d.email || "no email"} · {d.deal}{d.status !== "sent" && d.error ? ` · ${d.error}` : ""}</span>
                    </div>
                    <span className={`mono text-[9px] uppercase tracking-[0.15em] px-2 py-0.5 border ${d.status === "sent" ? "border-green-600 text-green-700" : d.status === "failed" ? "border-red-500 text-red-600" : "border-neutral-300 text-muted-foreground"}`}>{d.status}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}


function EmailSection({ people }) {
  const [investors, setInvestors] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [dealsList, setDealsList] = useState([]);
  const [dealSel, setDealSel] = useState("");
  const [includeLawyer, setIncludeLawyer] = useState(true);
  const [to, setTo] = useState([]);
  const [cc, setCc] = useState([]);
  const [bcc, setBcc] = useState([]);
  const [newEmail, setNewEmail] = useState("");
  const [newField, setNewField] = useState("to");
  const [subject, setSubject] = useState("");
  const [bodyText, setBodyText] = useState("");
  const [replyTo, setReplyTo] = useState("ralph@canihavemoney.com");
  const [sending, setSending] = useState(false);
  const [files, setFiles] = useState([]);
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [tplName, setTplName] = useState("");
  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);
  const [toInput, setToInput] = useState("");
  const [ccInput, setCcInput] = useState("");
  const [bccInput, setBccInput] = useState("");

  const loadTemplates = () => adminApi.get("/site/admin/templates").then(({ data }) => setTemplates(data.templates)).catch(() => {});
  useEffect(() => {
    adminApi.get("/site/admin/investors").then(({ data }) => setInvestors(data.investors.filter((i) => i.email))).catch(() => {});
    adminApi.get("/site/admin/deals").then(({ data }) => setDealsList(data.deals)).catch(() => {});
    loadTemplates();
  }, []);
  const loadDealRecipients = async () => {
    if (!dealSel) return toast.error("Choose a deal");
    try {
      const { data } = await adminApi.get(`/site/admin/deals/${dealSel}/investments`);
      const emails = [...new Set(data.investments.map((i) => i.email).filter(Boolean))];
      if (!emails.length) return toast.error("No investor emails in this deal");
      setTo([people?.sponsor_email, includeLawyer ? people?.lawyer_email : null].filter(Boolean));
      setCc([]); setBcc(emails);
      setShowBcc(true);
      setReplyTo(people?.sponsor_email || "");
      toast.success(`BCC set to ${emails.length} investor(s)`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const toggleLawyer = () => {
    const lw = people?.lawyer_email;
    const sp = people?.sponsor_email || "";
    const next = !includeLawyer;
    setIncludeLawyer(next);
    if (lw) { if (next) assign(lw, "to"); else removeRecipient(lw); }
    setReplyTo(sp);
  };

  const allTemplates = [...BUILTIN_TEMPLATES.filter((b) => !templates.some((t) => t.name === b.name)), ...templates];
  const setterFor = { to: setTo, cc: setCc, bcc: setBcc };
  const fieldOf = (email) => (to.includes(email) ? "to" : cc.includes(email) ? "cc" : bcc.includes(email) ? "bcc" : null);
  const assign = (email, targetField) => {
    const e = email.trim();
    if (!e) return;
    setTo((l) => l.filter((x) => x !== e));
    setCc((l) => l.filter((x) => x !== e));
    setBcc((l) => l.filter((x) => x !== e));
    if (targetField) setterFor[targetField]((l) => [...new Set([...l, e])]);
  };
  const toggleInvestor = (email, targetField) => assign(email, fieldOf(email) === targetField ? null : targetField);
  const removeRecipient = (email) => assign(email, null);
  const clearAll = () => { setTo([]); setCc([]); setBcc([]); };
  const addEmails = (fieldKey, raw, setInput) => {
    raw.split(/[,\n;\s]+/).map((x) => x.trim()).filter(Boolean).forEach((e) => assign(e, fieldKey));
    setInput("");
  };
  const applyTemplate = (key) => { const t = allTemplates.find((x) => x.name === key); if (t) { setSubject(t.subject); setBodyText(t.body); setTplName(t.name); } };

  const MAX_TOTAL = 3.5 * 1024 * 1024;
  const readFile = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(",")[1] || "");
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
  const onFiles = async (fileList) => {
    const incoming = Array.from(fileList || []);
    if (incoming.length === 0) return;
    const current = files.reduce((s, f) => s + f.size, 0);
    if (current + incoming.reduce((s, f) => s + f.size, 0) > MAX_TOTAL) return toast.error("Attachments exceed 3.5MB total (email provider limit)");
    try {
      const encoded = await Promise.all(incoming.map(async (f) => ({ filename: f.name, size: f.size, content: await readFile(f) })));
      setFiles((prev) => [...prev, ...encoded]);
    } catch { toast.error("Could not read file"); }
  };
  const removeFile = (name) => setFiles((prev) => prev.filter((f) => f.filename !== name));
  const fmtSize = (b) => (b < 1024 * 1024 ? `${Math.round(b / 1024)} KB` : `${(b / 1024 / 1024).toFixed(1)} MB`);

  const saveTemplate = async () => {
    if (!tplName.trim()) return toast.error("Enter a template name");
    const exists = templates.some((t) => t.name === tplName.trim());
    try { await adminApi.post("/site/admin/templates", { name: tplName.trim(), subject, body: bodyText }); toast.success(exists ? "Template updated" : "Template saved"); loadTemplates(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const deleteTemplate = async (name) => {
    if (!window.confirm(`Delete template "${name}"?`)) return;
    try { await adminApi.delete(`/site/admin/templates/${encodeURIComponent(name)}`); toast.success("Template deleted"); loadTemplates(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const genAI = async () => {
    if (!aiPrompt.trim()) return toast.error("Describe the email you want");
    setAiLoading(true);
    try {
      const { data } = await adminApi.post("/site/admin/email/generate", { prompt: aiPrompt });
      if (data.subject) setSubject(data.subject);
      if (data.body) setBodyText(data.body);
      toast.success("Draft generated — review, edit and send");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setAiLoading(false); }
  };

  const send = async () => {
    if (to.length + cc.length + bcc.length === 0) return toast.error("Add at least one recipient");
    if (!subject.trim()) return toast.error("Enter a subject");
    setSending(true);
    try {
      const attachments = files.map((f) => ({ filename: f.filename, content: f.content }));
      const res = await adminApi.post("/site/admin/email", { to, cc, bcc, subject, body: bodyText, reply_to: replyTo, attachments });
      toast.success(`Email sent to ${res.data.recipients} recipient(s)`);
      clearAll(); setFiles([]);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSending(false); }
  };

  const FieldRow = (label, list, fieldKey, input, setInput, right) => (
    <div className="flex items-start gap-2 border-b border-border px-4 py-1.5">
      <span className="mono text-[11px] uppercase tracking-[0.15em] text-muted-foreground w-9 pt-2 shrink-0">{label}</span>
      <div className="flex-1 flex flex-wrap items-center gap-1.5 py-1">
        {list.map((e) => (
          <span key={e} className="inline-flex items-center gap-1 bg-black text-white mono text-[11px] px-2 py-0.5">
            {e}<button data-testid={`chip-remove-${e}`} onClick={() => removeRecipient(e)} className="hover:text-neutral-400"><X size={11} /></button>
          </span>
        ))}
        <input data-testid={`${fieldKey}-input`} value={input} onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === ",") { e.preventDefault(); addEmails(fieldKey, input, setInput); } }}
          onBlur={() => addEmails(fieldKey, input, setInput)}
          className="flex-1 min-w-[160px] h-8 bg-transparent text-sm focus:outline-none" placeholder="Add email…" />
      </div>
      {right}
    </div>
  );

  const investorPicker = (
    <select data-testid="to-investors" value="" onChange={(e) => { if (e.target.value) assign(e.target.value, "to"); }} className="h-8 border border-black bg-background mono text-[10px] uppercase tracking-[0.1em] px-1 shrink-0 focus:outline-none">
      <option value="">+ Investor</option>
      {investors.map((c) => <option key={c._id} value={c.email}>{c.name}</option>)}
    </select>
  );

  return (
    <div data-testid="admin-email">
      <h1 className="text-3xl sm:text-4xl font-black tracking-tighter pb-3 border-b border-black">Send Email</h1>

      <DailyEmailPanel />

      <div className="max-w-2xl mt-6 border border-black p-3 flex flex-wrap items-center gap-2">
        <span className="mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Quick fill</span>
        <select data-testid="whole-deal-select" value={dealSel} onChange={(e) => setDealSel(e.target.value)} className="rounded-none border border-black bg-background h-9 px-2 mono text-[11px] focus:outline-none">
          <option value="">— Deal —</option>
          {dealsList.map((d) => <option key={d._id} value={d._id}>{d.name}</option>)}
        </select>
        <button type="button" data-testid="whole-deal-load" onClick={loadDealRecipients} className="inline-flex items-center gap-2 rounded-none bg-black text-white hover:bg-white hover:text-black border border-black transition-colors h-9 px-4 mono text-[10px] uppercase tracking-[0.15em]">{bcc.length > 0 && <Check size={12} />} BCC deal investors</button>
        <button type="button" data-testid="toggle-lawyer" onClick={toggleLawyer} className={`inline-flex items-center gap-2 rounded-none border border-black h-9 px-3 mono text-[10px] uppercase tracking-[0.15em] transition-colors ${includeLawyer ? "bg-black text-white" : "hover:bg-muted"}`}>
          {includeLawyer ? <Check size={12} /> : <span className="w-2.5 h-2.5 border border-current" />} Lawyer {includeLawyer ? "On" : "Off"}
        </button>
      </div>

      <div data-testid="ai-email-box" className="max-w-2xl mt-4 border border-black bg-black text-white p-4">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles size={15} />
          <span className="mono text-xs uppercase tracking-[0.25em]">Write with AI</span>
        </div>
        <textarea
          data-testid="ai-prompt"
          value={aiPrompt}
          onChange={(e) => setAiPrompt(e.target.value)}
          rows={2}
          placeholder="Describe the email… e.g. 'a warm update that the deal is fully funded and thank investors'"
          className="w-full rounded-none bg-white/10 border border-white/25 text-white placeholder:text-white/40 text-sm p-3 focus:outline-none focus:border-white resize-none"
        />
        <div className="flex items-center justify-between gap-3 mt-3">
          <span className="mono text-[10px] text-white/50">Generates a subject + body you can edit before sending.</span>
          <button
            type="button"
            data-testid="ai-generate"
            onClick={genAI}
            disabled={aiLoading}
            className="inline-flex items-center gap-2 rounded-none bg-white text-black hover:bg-neutral-200 transition-colors h-9 px-5 mono text-[10px] uppercase tracking-[0.2em] disabled:opacity-60"
          >
            <Sparkles size={12} /> {aiLoading ? "Writing…" : "Generate Draft"}
          </button>
        </div>
      </div>

      <div data-testid="email-composer" className="max-w-2xl mt-4 border border-black bg-background">
        <div className="bg-black text-white px-4 py-3 flex items-center justify-between">
          <span className="mono text-xs uppercase tracking-[0.2em]">New Message</span>
          <div className="flex items-center gap-4">
            <button type="button" data-testid="toggle-cc" onClick={() => setShowCc((v) => !v)} className={`mono text-[11px] uppercase tracking-[0.15em] ${showCc ? "text-white" : "text-neutral-400 hover:text-white"}`}>Cc</button>
            <button type="button" data-testid="toggle-bcc" onClick={() => setShowBcc((v) => !v)} className={`mono text-[11px] uppercase tracking-[0.15em] ${showBcc ? "text-white" : "text-neutral-400 hover:text-white"}`}>Bcc</button>
          </div>
        </div>

        {FieldRow("To", to, "to", toInput, setToInput, investorPicker)}
        {showCc && FieldRow("Cc", cc, "cc", ccInput, setCcInput, null)}
        {showBcc && FieldRow("Bcc", bcc, "bcc", bccInput, setBccInput, null)}

        <div className="flex items-center gap-2 border-b border-border px-4">
          <span className="mono text-[11px] uppercase tracking-[0.15em] text-muted-foreground w-9 shrink-0">Subj</span>
          <input data-testid="email-subject" value={subject} onChange={(e) => setSubject(e.target.value)} className="flex-1 h-11 bg-transparent text-sm focus:outline-none" placeholder="Subject" />
        </div>

        <textarea data-testid="email-body" value={bodyText} onChange={(e) => setBodyText(e.target.value)} rows={12} className="w-full bg-transparent p-4 text-sm leading-relaxed focus:outline-none resize-y" placeholder="Write your message…" />

        {files.length > 0 && (
          <div data-testid="attach-list" className="border-t border-border divide-y divide-border">
            {files.map((f) => (
              <div key={f.filename} className="flex items-center justify-between gap-3 px-4 py-2">
                <div className="flex items-center gap-2 min-w-0"><Paperclip size={13} className="shrink-0 text-muted-foreground" /><span className="text-sm truncate">{f.filename}</span><span className="mono text-[11px] text-muted-foreground shrink-0">{fmtSize(f.size)}</span></div>
                <button type="button" onClick={() => removeFile(f.filename)} className="text-muted-foreground hover:text-destructive shrink-0"><X size={15} /></button>
              </div>
            ))}
          </div>
        )}

        <div className="flex items-center justify-between gap-3 px-4 py-3 border-t border-black">
          <Button data-testid="email-send" onClick={send} disabled={sending} className="rounded-none bg-black text-white hover:bg-white hover:text-black border border-black transition-colors h-11 px-8 mono text-xs uppercase tracking-[0.2em]">
            {sending ? "Sending…" : "Send"}
          </Button>
          <div className="flex items-center gap-4">
            <select data-testid="email-template" value="" onChange={(e) => applyTemplate(e.target.value)} className="h-9 border border-black bg-background mono text-[10px] uppercase tracking-[0.1em] px-2 focus:outline-none">
              <option value="">Template</option>
              {allTemplates.map((t) => <option key={t.name} value={t.name}>{t.name}</option>)}
            </select>
            <label data-testid="attach-dropzone" title="Attach files (max 3.5MB total)" className="cursor-pointer text-muted-foreground hover:text-black">
              <Paperclip size={18} />
              <input data-testid="attach-input" type="file" multiple className="hidden" onChange={(e) => { onFiles(e.target.files); e.target.value = ""; }} />
            </label>
          </div>
        </div>
      </div>

      <details className="max-w-2xl mt-3">
        <summary className="mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground cursor-pointer">More options — reply-to & templates</summary>
        <div className="border border-black p-4 mt-2 space-y-4">
          <div>
            <Label className="mono text-xs uppercase tracking-[0.2em]">Reply-To</Label>
            <Input data-testid="email-replyto" value={replyTo} onChange={(e) => setReplyTo(e.target.value)} className={`${field} mt-2`} placeholder="ralph@canihavemoney.com" />
            <p className="mono text-[10px] text-muted-foreground mt-2">Sends from the platform address, but replies go here.</p>
          </div>
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[200px]">
              <Label className="mono text-xs uppercase tracking-[0.2em]">Save or update a template</Label>
              <Input data-testid="tpl-name" value={tplName} onChange={(e) => setTplName(e.target.value)} className={`${field} mt-2`} placeholder="Template name" />
              <p className="mono text-[10px] text-muted-foreground mt-2">Use an existing name to update it, or a new name to save a new template.</p>
            </div>
            <button data-testid="tpl-save" onClick={saveTemplate} className="rounded-none border border-black h-11 px-6 mono text-xs uppercase tracking-[0.2em] hover:bg-black hover:text-white transition-colors">Save / Update</button>
          </div>
          {templates.length > 0 && (
            <div>
              <Label className="mono text-xs uppercase tracking-[0.2em]">Saved templates — select to edit</Label>
              <div className="flex items-center gap-2 mt-2">
                <select data-testid="tpl-edit-select" value={templates.some((t) => t.name === tplName) ? tplName : ""} onChange={(e) => { if (e.target.value) applyTemplate(e.target.value); }} className="flex-1 min-w-[200px] rounded-none border border-black bg-background h-11 px-2 mono text-[11px] focus:outline-none">
                  <option value="">— Select a saved template —</option>
                  {templates.map((t) => <option key={t.name} value={t.name}>{t.name}</option>)}
                </select>
                <button data-testid="tpl-delete-selected" onClick={() => { if (templates.some((t) => t.name === tplName)) deleteTemplate(tplName); else toast.error("Select a saved template to delete"); }} className="rounded-none border border-black h-11 px-4 mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground hover:bg-destructive hover:text-white hover:border-destructive transition-colors">Delete</button>
              </div>
            </div>
          )}
        </div>
      </details>
    </div>
  );
}

/* ---------------- WIRE ---------------- */
function WireSection() {
  const [open, setOpen] = useState(false);
  const [unlocked, setUnlocked] = useState(false);
  return (
    <div data-testid="admin-wire">
      <button data-testid="wire-toggle" onClick={() => { setOpen((o) => !o); setUnlocked(false); }} className="w-full flex items-center justify-between text-3xl sm:text-4xl font-black tracking-tighter pb-3 border-b border-black">
        <span>Company Account</span>
        <ChevronDown size={24} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (unlocked ? <WireEditor /> : <WireLock onUnlock={() => setUnlocked(true)} />)}
    </div>
  );
}

function WireLock({ onUnlock }) {
  const [code, setCode] = useState("");
  const submit = (e) => {
    e.preventDefault();
    if (code.trim() === "1408") onUnlock();
    else toast.error("Incorrect edit code");
  };
  return (
    <form onSubmit={submit} data-testid="wire-lock" className="border border-black p-6 max-w-sm mt-6">
      <div className="mono text-xs uppercase tracking-[0.2em] text-muted-foreground flex items-center gap-2"><Lock size={12} /> Locked — enter edit code</div>
      <Input data-testid="wire-code" type="password" value={code} onChange={(e) => setCode(e.target.value)} className={`${field} mt-3`} placeholder="••••" autoFocus />
      <Button type="submit" data-testid="wire-unlock" className="mt-4 rounded-none bg-black text-white hover:bg-white hover:text-black border border-black transition-colors h-11 px-8 mono text-xs uppercase tracking-[0.2em]">Unlock to Edit</Button>
    </form>
  );
}

function WireEditor() {
  const [w, setW] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { adminApi.get("/site/admin/wire").then(({ data }) => setW(data || {})); }, []);
  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try { await adminApi.put("/site/wire", w); toast.success("Wire instructions saved"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  if (!w) return <div className="mono text-xs text-muted-foreground animate-pulse mt-6">Loading…</div>;
  return (
    <form onSubmit={save} data-testid="admin-wire-form" className="max-w-2xl mt-6">
      <p className="mono text-xs text-muted-foreground mb-8">These details appear to an investor after they submit a commitment.</p>
      <div className="grid sm:grid-cols-2 gap-6">
        {WIRE_FIELDS.map(([key, label]) => (
          <div key={key} className={key === "notes" || key === "bank_address" ? "sm:col-span-2" : ""}>
            <Label className="mono text-xs uppercase tracking-[0.2em]">{label}</Label>
            <Input data-testid={`wire-${key}`} value={w[key] || ""} onChange={(e) => setW({ ...w, [key]: e.target.value })} className={`${field} mt-2`} />
          </div>
        ))}
      </div>
      <Button type="submit" data-testid="admin-wire-save" disabled={saving} className="mt-8 rounded-none bg-black text-white hover:bg-white hover:text-black border border-black transition-colors h-12 px-10 mono text-xs uppercase tracking-[0.25em]">
        {saving ? "Saving…" : "Save Instructions"}
      </Button>
    </form>
  );
}

function AdminLogin({ onBack, onAuthed }) {
  const [code, setCode] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/gate/admin-verify", { code: code.trim() });
      localStorage.setItem("admin_token", data.token);
      onAuthed();
    } catch { setErr("Incorrect admin code"); }
    finally { setBusy(false); }
  };
  return (
    <div className="min-h-screen bg-black text-white flex items-center justify-center px-6 grain relative">
      <form onSubmit={submit} className="relative z-10 w-full max-w-sm">
        <div className="mono text-sm font-semibold tracking-[0.2em] text-center">CANIHAVEMONEY&nbsp;LLC</div>
        <div className="mono text-[10px] uppercase tracking-[0.3em] text-neutral-500 mt-3 mb-10 flex items-center justify-center gap-2">
          <Lock size={11} /> Admin Access
        </div>
        <Label className="mono text-xs uppercase tracking-[0.2em] text-neutral-400">Admin Code</Label>
        <Input data-testid="admin-code" type="password" autoFocus value={code} onChange={(e) => setCode(e.target.value)} className="rounded-none bg-transparent border-white/30 text-white h-12 mt-2 focus-visible:ring-0 focus-visible:border-white" placeholder="••••" />
        {err && <p data-testid="admin-login-error" className="mono text-xs text-red-500 mt-3">{err}</p>}
        <Button type="submit" data-testid="admin-login-submit" disabled={busy} className="w-full mt-6 rounded-none bg-white text-black hover:bg-transparent hover:text-white border border-white transition-colors h-12 mono text-xs uppercase tracking-[0.25em]">
          {busy ? "Checking…" : "Enter Dashboard"}
        </Button>
        <button type="button" onClick={onBack} className="w-full mt-4 mono text-[10px] uppercase tracking-[0.2em] text-neutral-500 hover:text-white">Back to home</button>
      </form>
    </div>
  );
}
